/**
 * SampleRegistrationDetailPage — the Sample Registration detail view
 * (Class C transaction BO), built from the approved design prototype
 * `29131-AI_Sample Registration/Sample-Tracking-Module-Prototype.html`
 * (2026-07-18) and the legacy XAF view
 * `Samplecheckin_DetailView_Copy_SampleRegistration`.
 *
 * ── ANATOMY (BO UI Standard §5 · Class C) ───────────────────────────────────
 *   · breadcrumb → <FormTitle> (nav icon/colour + load chip + audit chip)
 *   · the shared <StepIsland>, portalled into the AppShell top bar:
 *       Registration · Samples · Tests · Containers · Save · Submit
 *     Step state is computed LIVE from the data (never a static wizard).
 *   · NO step-hint banner — the prototype hides hints app-wide (2026-07-18:
 *     "not very useful once the user knows what to do, the stepper is enough")
 *   · the XAF action bar: form-specific actions LEFT · Save · Save and Close ·
 *     Save and New · | · Cancel · Back/Next — top, and repeated at the bottom
 *     on the long Registration step only
 *   · the step body: Registration (3 field sections + 7 tabs) or the Sample
 *     Login workspace (Sample Login / Test View / Containers / Parameters) or
 *     the Save / Submit review steps
 *   · the flow bar: Samples · Tests · Containers CTAs with live counts
 *
 * ── WHAT IS AND IS NOT WIRED (AI-29131, data-source handoff) ─────────────────
 * The list view, detail view and lookup catalogs now read real data:
 *   · GET /api/v1/sample-registration
 *   · GET /api/v1/sample-registration/{jobId}
 *   · GET /api/v1/sample-registration/lookups?matrixName=
 *   · GET /api/v1/lookups/employees  — the GLOBAL employee lookup that feeds
 *     "Received By" (shared by every form, not owned by this one)
 * Save / submit / delete and child-collection persistence are still TODO:
 * every persistence point remains funnelled through `buildPayload()` + `persist()`
 * so wiring the write endpoints stays surgical.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouse } from 'react';
import { api } from '../../../api/client';
import { fetchEmployeeLookup } from '../../../api/lookups';
import { FormTitle } from '../../../components/FormTitle';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { SAMPLE_REGISTRATION_AUDIT_FORMS } from '../../audit/aiAuditForms';
import { TopbarStepper } from '../../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../../components/StepIsland/StepIsland';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { LoaderSymbol } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useSaveFlow, warn as warnDialog } from '../../../components/confirm';
import { confirmDiscardIfUnsaved, registerUnsavedGuard, resetUnsavedTracking } from '../../../hooks/useUnsavedGuard';
import { focusFirstFormControl } from '../../../hooks/useAutoFocusFirstControl';
import { useNavActions } from '../../../hooks/useNavActions';
import { inertProps } from './inertProps';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { RegistrationForm } from './RegistrationForm';
import { RegistrationTabs } from './RegistrationTabs';
import { SampleLoginView, type SlTab, cachedParamOid, cachedParams } from './SampleLoginView';
import { GenericSampleLabelDialog } from './GenericSampleLabelDialog';
import { SampleContainerPhCheckDialog } from './SampleContainerPhCheckDialog';
import { CoolerIdDialog } from './CoolerIdDialog';
import { NewLookupDialog } from './NewLookupDialog';
import {
  LOOKUPS, MULTI_LOOKUPS, SAMPLE_STATUS_OPTS, autoBottles, chargeRows, cloneTest, emptyHeader,
  copyTest, emptyTabState, findDuplicateAnalyses, mkTest, nextKey, renumber, resolveDueDate, sampleId, toDateInput, toDateTimeInput,
  eddFormatToInt, eddFormatFromInt,
  SCC_POINTS,
  type SrAttachment, type SrHeader, type SrImage, type SrItemCharge,
  type SrLookup, type SrLookupKey, type SrNote, type SrReceivedDate,
  type SrSample, type SrSccComment, type SrSccRecord, type SrTabState, type SrTest,
} from './srData';
import './sampleRegistration.css';

/** The six workflow steps (prototype STEPS). */
const STEPS = ['Registration', 'Samples', 'Tests', 'Containers', 'Save', 'Submit'] as const;

/** Tooltip on an action the user's Module Access Control rights do not allow. */
const NO_RIGHT_TIP = 'Your access level does not allow this action.';
export type StepIndex = 0 | 1 | 2 | 3 | 4 | 5;

/** The audit FormName for this form (§14b.2 registry). */
export const SAMPLE_REGISTRATION_FORM = 'Sample Registration';

/** Status a just-SAVED job carries until it is submitted. Legacy
 *  SampleRegistrationViewController (~L5749): a Regular job saves as SR-2-2 "Pending
 *  Submission", a Pre-scheduled job as SR-1-1 "Pending Sampling" — the Save SP already
 *  stores that, so the label shown after Save must match it. */
const pendingStatusFor = (registrationType?: string): string =>
  registrationType === 'Pre-scheduled' ? 'Pending Sampling' : 'Pending Submission';

/** One Reports ▾ item — the shared `FormReportItem` from
 *  GET /api/v1/sample-registration/reports (Custom Report Builder). */
interface FormReportItem {
  id: number;
  /** Menu caption (CustomCaption ?? UserDefinedName ?? designer name). */
  label: string;
  /** `colCustomReportDesignerName` — the layout name the renderer loads. */
  templateName: string;
  allowMultipleJobId: boolean;
}

interface ApiSuccess<T> {
  success: true;
  data: T;
}

interface SampleRegistrationDetailDto {
  jobId: string;
  oid: string;
  receivedDate: string;
  receivedBy: string;
  createdBy: string;
  client: string;
  clientOid: string;
  clientAddress: string;
  clientPhone: string;
  clientContact: string;
  accountNumber: string;
  quoteId: string;
  poNumber: string;
  paymentStatus: string;
  manifestNumber: number | null;
  projectManager: string;
  dateExpect: string;
  relinquishedBy: string;
  collector: string;
  dateCollected: string;
  timeCollected: string;
  projectId: string;
  projectOid: string;
  projectName: string;
  projectLocation: string;
  permit: string;
  projectCategory: string;
  cocId: string;
  cocSource: string;
  invoiceContact: string;
  registrationType: string;
  jobInactivation: string;
  cancelJob: boolean;
  cancelStatus: number;
  signoffStatus?: number;
  statusName: string;
  matrices: string;
  testTemplate: string;
  sampleCategory: string;
  coolerId: string;
  noOfSamples: number;
  remark: string;
  tat: string;
  dueDate: string;
  reportTemplate: string;
  eddTemplate: string;
  eddFormat: string;
  reportDeliveryAddress: string;
  reportDeliveryMethod: string;
}

export interface SampleRegistrationLookupRowDto {
  lookupKey: string;
  col1: string;
  col2: string;
  col3: string;
  col4: string;
  // Oid string — the FK the API needs at Save time. Empty for text/enum lookups.
  col5?: string;
  // Metadata flag — 'test'/'testgroup' carry IsFieldTest ('1' = field test).
  col6?: string;
}

/** SampleLogIn row from GET .../{oid}/sample-logins (RS1). */
interface SampleLoginRowDto {
  jobIdStr:        string;
  oid:             string;
  sampleNo:        number;
  clientSampleId:  string | null;
  visualMatrix:    string | null;
  sampleType:      string | null;
  qty:             number | null;
  collector:       string | null;
  collectDate:     string | null;
  collectTime:     string | null;
  description:     string | null;
  comment:         string | null;
  qcCategory:      string | null;
  systemType:      string | null;
  siteNameOid:     string | null;
  hold:            boolean;
  subOut:          boolean;
  samplingStatus:  number;
  loginBy:         string | null;
  loginDateTime:   string | null;
  lastUpdatedBy:   string | null;
  lastUpdatedDate: string | null;
  // Additional configurable columns
  samplingLocation:     string | null;
  address:              string | null;
  keyMap:               string | null;
  samplingAddress:      string | null;
  samplePointID:        string | null;
  samplePointType:      string | null;
  pwsidOid:             string | null;
  waterSource:          string | null;
  rejectionCriteriaOid: string | null;
  repeatLocationOid:    string | null;
  departmentOid:        string | null;
  sampleStatusOid:      string | null;
  parentSampleID:       string | null;
  parentSampleDate:     string | null;
  sysSampleCode:        string | null;
  projectNumber:        string | null;
  plannedLatitude:      string | null;
  plannedLongitude:     string | null;
  chlorineFree:         string | null;
  chlorineTotal:        string | null;
  flowRate:             string | null;
  volume:               string | null;
  time:                 string | null;
  startFlow:            string | null;
  endFlow:              string | null;
  timeStart:            string | null;
  timeEnd:              string | null;
  remark:               string | null;
  collectorPhone:       string | null;
  samplingEvent:        string | null;
  alternativeStation:   string | null;
  excludeInvoice:       boolean;
  notReport:            boolean;
  complianceProject:    boolean;
  dueDate:              string | null;
  restTime:             string | null;
  ph:                   string | null;
  do:                   string | null;
  standardVol:          string | null;
  absorptionvol:        string | null;
  trafficFlowBigVechile:    string | null;
  trafficFlowMiddleVechile: string | null;
  trafficFlowSmallVechile:  string | null;
  receivedDateOid:      string | null;
  // Sample-Login lookup-master FK Oids (G5 parity 2026-09-30)
  bareOid:              string | null;
  kindOid:              string | null;
  codeOid:              string | null;
  componentsOid:        string | null;
  sizeOid:              string | null;
  sanitarianOid:        string | null;
  sanitarianTempOid:    string | null;
  totalSurfaceAreaOid:  string | null;
  airVolumeOid:         string | null;
  siteSourceOid:        string | null;
  sourceOid:            string | null;
  waterTypeOid:         string | null;
  pwsSystemNameOid:     string | null;
  userDefinedDropdown1Oid: string | null;
  userDefinedDropdown2Oid: string | null;
  userDefinedDropdown3Oid: string | null;
  userDefinedDropdown4Oid: string | null;
}

/** SampleParameter row from GET .../{oid}/sample-logins (RS2). */
interface SampleParamDto {
  sampleloginOid: string;
  oid:            string;
  testMethodOid:  string | null;
  testName:       string | null;
  methodNumber:   string | null;
  component:      string | null;
  testGroupName:  string | null;
  testHold:       boolean;
  tatOid:         string | null;
  tatName:        string | null;
  dateReceived:   string | null;
  subOut:         boolean;
  status:         number;
  isFieldTest:    boolean;
  // Analyte-level result columns (read-only display in the Parameters tab,
  // user request 2026-09-23). Blank until the lab enters them in Result Entry.
  parameterName:  string | null;
  result:         string | null;
  analyzedDate:   string | null;
  analyzedBy:     string | null;
  units:          string | null;
}

/** SampleBottleAllocation row from GET .../{oid}/sample-logins (RS3). One row per
 *  sample × testMethod × bottle letter. Container / Preservative / Storage /
 *  StorageCondition are display NAMES (matching the Containers dropdown values). */
interface SampleBottleAllocDto {
  sampleloginOid:   string;
  testMethodOid:    string | null;
  bottleId:         string;
  container:        string | null;
  preservative:     string | null;
  storage:          string | null;
  storageCondition: string | null;
}

/** Combined payload from GET .../{oid}/sample-logins. */
export interface SampleLoginsDto {
  samples:    SampleLoginRowDto[];
  parameters: SampleParamDto[];
  // RS3 — persisted bottle allocations, so reopening a saved job rehydrates the
  // BottleID / Container / Preservative / Storage instead of resetting to the "A"
  // default (round-cause A fix). May be absent on an older API build.
  bottles?:   SampleBottleAllocDto[];
}

/**
 * Build the in-memory {@link SrSample} rows from a persisted SampleLogin +
 * SampleParameter payload. Shared by the Edit-Mode load effect AND the
 * post-save refresh, so the flow-bar counts (Samples / Tests / Containers)
 * reflect what is actually in the DB after a save — the G5 parity behaviour
 * where those captions recompute from SampleLogIn / SampleParameter counts
 * (SampleRegistrationViewController.cs ~9619-9651). Returns [] when the job
 * has no persisted samples.
 */
export function mapSampleLoginsPayload(payload: SampleLoginsDto | null): SrSample[] {
  if (!payload || !payload.samples?.length) return [];

  // Group SampleParameter rows by their SampleLogIn Oid
  const paramsByOid = new Map<string, SampleParamDto[]>();
  for (const p of payload.parameters ?? []) {
    const arr = paramsByOid.get(p.sampleloginOid) ?? [];
    arr.push(p);
    paramsByOid.set(p.sampleloginOid, arr);
  }

  // Group persisted bottle allocations (RS3) by `${sampleLogInOid}|${testMethodOid}`
  // so each loaded test can rehydrate its BottleID(s) + Container / Preservative /
  // Storage exactly as saved (round-cause A). SampleBottleAllocation is keyed by
  // SampleRegistration + TestMethod — no component — so this matches the test's
  // TestMethodOid, applied to whichever grouped test row carries it.
  // GUID case-insensitive: RS3 casts TestMethod to an UPPERCASE string, while the
  // parameters' testMethodOid comes from Guid.ToString() (lowercase). Lowercase
  // both sides of the key so the join actually matches (else hydration no-ops and
  // BottleID silently stays 'A').
  const allocKey = (sampleOid: string, testMethodOid: string) =>
    `${sampleOid.toLowerCase()}|${testMethodOid.toLowerCase()}`;
  const allocByKey = new Map<string, SampleBottleAllocDto[]>();
  for (const b of payload.bottles ?? []) {
    if (!b.bottleId || !b.testMethodOid) continue;
    const k = allocKey(b.sampleloginOid, b.testMethodOid);
    const arr = allocByKey.get(k) ?? [];
    arr.push(b);
    allocByKey.set(k, arr);
  }

  return payload.samples.map(row => {
    const id = `${row.jobIdStr}-${String(row.sampleNo).padStart(3, '0')}`;
    const v: Record<string, string> = {
      _oid:            row.oid ?? '',
      SampleNo:        String(row.sampleNo),
      SampleName:      row.clientSampleId  ?? '',
      SampleMatrix:    row.visualMatrix     ?? '',
      SampleType:      row.sampleType       ?? '',
      Qty:             row.qty != null ? String(row.qty) : '1',
      // Legacy parity (SampleRegistrationBottleAllocationViewController line 1128:
      // `if (sampleReg.Qty == 0)`): a persisted non-zero Qty is the user's own
      // container count and must never be recomputed. Mark it manual so adding a
      // test / re-assigning bottles leaves it untouched (user report: "Qty showed 4,
      // dropped to 1 after adding a test" — autoBottles was re-deriving it).
      __qtyManual:     (row.qty != null && row.qty > 0) ? '1' : '',
      Collector:       row.collector        ?? '',
      DateCollected:   row.collectDate ? row.collectDate.slice(0, 10) : '',
      TimeCollected:   row.collectTime      ?? '',
      Description:     row.description      ?? '',
      Remark:          row.comment          ?? '',
      QCCategory:      row.qcCategory       ?? '',
      SystemType:      row.systemType       ?? '',
      SiteName:        row.siteNameOid      ?? '',
      Hold:            row.hold ? '1' : '0',
      SubOut:          row.subOut ? '1' : '0',
      SamplingStatus:  String(row.samplingStatus ?? 0),
      EnteredBy:       row.loginBy           ?? '',
      EnteredDate:     row.loginDateTime     ?? '',
      LastUpdatedBy:   row.lastUpdatedBy     ?? '',
      LastUpdatedDate: row.lastUpdatedDate   ?? '',
      // Additional configurable columns
      SamplingLocation:   row.samplingLocation   ?? '',
      Address:            row.address            ?? '',
      KeyMap:             row.keyMap             ?? '',
      SamplingAddress:    row.samplingAddress    ?? '',
      SamplePointID:      row.samplePointID     ?? '',
      SamplePointType:    row.samplePointType   ?? '',
      PWSID:              row.pwsidOid          ?? '',
      // WaterType is a FK to dbo.WaterTypes (G5 parity 2026-09-30) — hydrate the
      // OID so it resolves in the dropdown; the old text `waterSource` binding was
      // wrong (that is the separate free-text WaterSource column).
      WaterType:          row.waterTypeOid       ?? '',
      RejectionCriteria:  row.rejectionCriteriaOid ?? '',
      RepeatLocation:     row.repeatLocationOid ?? '',
      // Sample-Login lookup-master FK dropdowns (G5 parity 2026-09-30) — each is
      // the stored OID, resolved to a display name via config.extraLookups[FieldID].
      Bare:               row.bareOid            ?? '',
      Kind:               row.kindOid            ?? '',
      Code:               row.codeOid            ?? '',
      Components:         row.componentsOid      ?? '',
      Size:               row.sizeOid            ?? '',
      Sanitarian:         row.sanitarianOid      ?? '',
      SanitarianTemp:     row.sanitarianTempOid  ?? '',
      TotalSurfaceArea:   row.totalSurfaceAreaOid ?? '',
      AirVolume:          row.airVolumeOid       ?? '',
      SiteSource:         row.siteSourceOid      ?? '',
      Source:             row.sourceOid          ?? '',
      PWSSystemName:      row.pwsSystemNameOid   ?? '',
      UserDefinedDropdown1: row.userDefinedDropdown1Oid ?? '',
      UserDefinedDropdown2: row.userDefinedDropdown2Oid ?? '',
      UserDefinedDropdown3: row.userDefinedDropdown3Oid ?? '',
      UserDefinedDropdown4: row.userDefinedDropdown4Oid ?? '',
      Department:         row.departmentOid     ?? '',
      SampleStatus:       row.sampleStatusOid   ?? '',
      ParentSampleID:     row.parentSampleID    ?? '',
      ParentSampleDate:   row.parentSampleDate  ?? '',
      SysSampleCode:      row.sysSampleCode     ?? '',
      ProjectNumber:      row.projectNumber     ?? '',
      PlannedLatitude:    row.plannedLatitude    ?? '',
      PlannedLongitude:   row.plannedLongitude   ?? '',
      'FreeColorine(mg/L)': row.chlorineFree    ?? '',
      'TotalChlorine(mg/L)': row.chlorineTotal  ?? '',
      FlowRate:           row.flowRate           ?? '',
      Volume:             row.volume             ?? '',
      Time:               row.time               ?? '',
      StartFlow:          row.startFlow          ?? '',
      EndFlow:            row.endFlow            ?? '',
      TimeStart:          row.timeStart          ?? '',
      TimeEnd:            row.timeEnd            ?? '',
      Comment:            row.comment            ?? '',
      CollectorPhone:     row.collectorPhone     ?? '',
      SamplingEvent:      row.samplingEvent      ?? '',
      AlternativeStation: row.alternativeStation ?? '',
      ExcludeInvoice:     row.excludeInvoice ? '1' : '0',
      NotReport:          row.notReport ? '1' : '0',
      ComplianceProject:  row.complianceProject ? '1' : '0',
      DueDate:            row.dueDate ? row.dueDate.slice(0, 10) : '',
      RestTime:           row.restTime           ?? '',
      PH:                 row.ph                 ?? '',
      DO:                 (row as unknown as Record<string, string>)['do'] ?? '',
      StandardVol:        row.standardVol        ?? '',
      Absorptionvol:      row.absorptionvol      ?? '',
      TrafficFlowBigVechile:    row.trafficFlowBigVechile    ?? '',
      TrafficFlowMiddleVechile: row.trafficFlowMiddleVechile ?? '',
      TrafficFlowSmallVechile:  row.trafficFlowSmallVechile  ?? '',
      ReceivedDate:       row.receivedDateOid    ?? '',
    };

    const params = paramsByOid.get(row.oid) ?? [];
    // Group SampleParameter rows by TestMethod+Component so the
    // grid shows ONE row per test+component, not one per analyte.
    const grouped = new Map<string, SampleParamDto>();
    for (const p of params) {
      if (!p.testMethodOid || !p.testName) continue;
      const gk = `${p.testMethodOid}|${p.component ?? ''}`;
      if (!grouped.has(gk)) grouped.set(gk, p);
    }
    const tests: SrTest[] = [...grouped.values()].map(p => {
      const t = mkTest(
        p.testName!,
        p.methodNumber ?? '',
        p.component    ?? '',
        '',
        p.testGroupName ?? '',
      );
      t.key           = `${p.testMethodOid}|${p.component ?? ''}`;
      t.hold          = p.testHold;
      t.status        = SAMPLE_STATUS_OPTS[p.status ?? 0] ?? '';
      // Status "Sub Out" implies the SubOut flag — also repairs rows saved before the
      // save fix (Status=Sub Out but SubOut=0).
      t.subOut        = p.subOut || t.status === 'Sub Out';
      t.testMethodOid = p.testMethodOid ?? undefined;
      // Preserve tatOid from DB so persist() can fall back when customRequest has no TAT override
      t.tatOid = p.tatOid ?? null;
      t.isFieldTest = p.isFieldTest || undefined;
      if (t.isFieldTest) t.bottle = '';   // field tests carry no bottle (legacy parity)
      // ── Round-cause A: rehydrate the saved bottle setup ──────────────────────
      // Before this, mkTest hard-defaulted bottle:'A' + the test's static default
      // container/preserv, so a reopened saved job lost its BottleID / Container /
      // Preservative (they ARE persisted to dbo.SampleBottleAllocation, but the GET
      // never read them back). Now RS3 carries them, keyed by sampleLogIn+testMethod.
      const allocs = p.testMethodOid ? allocByKey.get(allocKey(row.oid, p.testMethodOid)) : undefined;
      if (allocs && allocs.length > 0) {
        // dbo.SampleBottleAllocation has no Component column, so it is keyed by
        // (sample, testMethod, bottleId). A test with several components shares ONE
        // testMethodOid, so allocByKey returns EVERY component's allocation for this
        // method — dedupe by BottleID so a 2-component test on bottle "A" hydrates as
        // "A", not "A,A" (which rendered as duplicate Container rows on reload —
        // user report 2026-09-30: 2 Metals components on bottle A showed 4 rows).
        const byLetter = new Map<string, typeof allocs[number]>();
        for (const a of allocs) if (a.bottleId && !byLetter.has(a.bottleId)) byLetter.set(a.bottleId, a);
        const sorted = [...byLetter.values()].sort((a, b) => a.bottleId.localeCompare(b.bottleId));
        t.bottle = sorted.map(a => a.bottleId).join(',');
        const bd: Record<string, { container: string; preserv: string; storage: string; storageCondition: string }> = {};
        for (const a of sorted) {
          bd[a.bottleId] = {
            container:        a.container        ?? '',
            preserv:          a.preservative     ?? '',
            storage:          a.storage          ?? '',
            storageCondition: a.storageCondition ?? '',
          };
        }
        t.bd = bd;
        // Test-level fields mirror the FIRST bottle — the same value the save reads
        // as its fallback (persist(): `detail?.container ?? t.container`), so the
        // Containers dropdowns and Test-Assignment show the saved value, not 'A'.
        const first = sorted[0]!;
        t.container = first.container    ?? '';
        t.preserv   = first.preservative ?? '';
        t.storage   = first.storage      ?? '';
        // This test's bottle setup is authoritative (persisted) — the guide effect
        // and autoBottles must not overwrite it (round-cause B).
        t.hasSavedBottle = true;
      }
      // Hydrate per-analyte results (read-only display in the
      // Parameters tab, user request 2026-09-23): every SampleParameter
      // row of THIS test+component, keyed by its ParameterName so the
      // grid's per-analyte rows show Result / AnalysisDate / AnalyzedBy /
      // Units entered later in Result Entry (blank until analysed).
      const gk = `${p.testMethodOid}|${p.component ?? ''}`;
      const savedNames = new Set<string>();
      for (const pp of params) {
        if (`${pp.testMethodOid}|${pp.component ?? ''}` !== gk) continue;
        if (!pp.parameterName) continue;
        savedNames.add(pp.parameterName);
        t.paramData[pp.parameterName] = {
          result:       pp.result       ?? '',
          units:        pp.units        ?? '',
          analysisDate: pp.analyzedDate ?? '',
          analyzedBy:   pp.analyzedBy   ?? '',
        };
      }
      // Remember WHICH analytes were saved so a customized (subset) selection survives a
      // reload / the post-save re-hydrate. Without it params stayed null → "All Param".
      if (savedNames.size > 0) t.savedParamNames = [...savedNames];
      return t;
    });

    return { id, hold: row.hold, v, tests };
  });
}

/** One Custom Request override row from GET .../{oid}/custom-requests. */
interface CustomRequestRowDto {
  testName: string;
  tatName:  string | null;
  tatOid:   string | null;
  dueDate:  string | null;
  hold:     boolean;
  subOut:   boolean;
}

/** One Received Dates row from GET .../{oid}/received-dates (FK cols resolved). */
interface ReceivedDateRowDto {
  oid: string;
  receivedDate: string | null;
  receivedBy: string;
  relinquishedBy: string;
  tat: string;
  sampleMatrix: string;
  sampleCategory: string;
  coolerId: string;
  noOfSamples: number;
  createdBy: string;
  createdDate: string | null;
}

/** Map an API received-date row → the sub-tab's SrReceivedDate shape. */
function mapReceivedDateRow(r: ReceivedDateRowDto): SrReceivedDate {
  return {
    key: `rd-srv-${r.oid}`,
    oid: r.oid,
    date: r.receivedDate ?? '',
    by: r.receivedBy ?? '',
    relinq: r.relinquishedBy ?? '',
    n: r.noOfSamples ?? 1,
    matrix: (r.sampleMatrix ?? '').split('; ').filter(Boolean),
    tat: r.tat ?? '',
    cooler: r.coolerId ?? '',
    cat: (r.sampleCategory ?? '').split('; ').filter(Boolean),
    createdBy: r.createdBy ?? '',
    created: r.createdDate ?? '',
  };
}

// ── Child-tab API DTOs + mapping helpers ───────────────────────────────────

interface NoteRowDto {
  oid: string;
  title: string;
  author: string;
  authorOid: string;
  date: string | null;
  sort: number;
  html: string;
  plainText: string;
  sourceId: string;
  noteSource: string;
}

function mapNoteRow(r: NoteRowDto): SrNote {
  return {
    key: `note-srv-${r.oid}`, oid: r.oid, title: r.title ?? '',
    author: r.author ?? '', authorOid: r.authorOid ?? '',
    date: r.date ?? '', sort: r.sort ?? 0,
    html: r.html ?? '', plainText: r.plainText ?? '',
    sourceId: r.sourceId ?? '', noteSource: r.noteSource ?? '',
  };
}

interface SccDto {
  oid: string;
  checkInBy: string;
  checkInDate: string | null;
  visualMatrix: string;
  temperature: string;
  correctedTemperature: string;
  thermometerID: string;
  samplePH: string;
  phpaperID: string;
  receivedDate: string;
  checkPoints: { checkPointName: string; isYes: boolean; isNo: boolean; isNA: boolean; description: string }[];
  comments: { comment: string; userName: string; commentDateTime: string | null }[];
}

function mapSccRow(r: SccDto): SrSccRecord {
  const pointMap: Record<string, string> = {};
  const descMap: Record<string, string> = {};
  for (const cp of r.checkPoints ?? []) {
    pointMap[cp.checkPointName] = cp.isYes ? 'yes' : cp.isNo ? 'no' : 'na';
    descMap[cp.checkPointName] = cp.description ?? '';
  }
  return {
    key: `scc-srv-${r.oid}`, oid: r.oid,
    matrix: r.visualMatrix ?? '', by: r.checkInBy ?? '',
    date: r.checkInDate ?? '', recv: r.receivedDate ?? '',
    temp: r.temperature ?? '', corr: r.correctedTemperature ?? '',
    thermo: r.thermometerID ?? '', ph: r.samplePH ?? '', paper: r.phpaperID ?? '',
    points: SCC_POINTS.map(p => pointMap[p[0]] ?? p[1]),
    descriptions: SCC_POINTS.map(p => descMap[p[0]] ?? ''),
    comments: (r.comments ?? []).map((c): SrSccComment => ({
      text: c.comment ?? '', user: c.userName ?? '', dt: c.commentDateTime ?? '',
    })),
  };
}

interface ItemChargeRowDto {
  oid: string;
  itemName: string;
  description: string;
  itemPriceOid: string;
  unitPrice: number | null;
  qty: number | null;
  amount: number | null;
  finalAmount: number | null;
  npUnitPrice: number | null;
  discount: number | null;
  remark: string;
}

function mapItemChargeRow(r: ItemChargeRowDto): SrItemCharge {
  return {
    key: `ic-srv-${r.oid}`, oid: r.oid,
    itemPriceOid: r.itemPriceOid ?? '',
    name: r.itemName ?? '',
    desc: r.description ?? '',
    price: r.unitPrice ?? 0, qty: r.qty ?? 0,
    amount: r.amount ?? 0, final: r.finalAmount ?? 0,
    npPrice: r.npUnitPrice ?? 0, disc: r.discount ?? 0,
    remark: r.remark ?? '',
  };
}

interface AttachmentRowDto {
  oid: string;
  name: string;
  category: number;
  operator: string;
  operatorOid: string;
  date: string | null;
  comment: string;
  fileDataOid: string;
  receivedDate: string;
  fileName: string;
}

function mapAttachmentRow(r: AttachmentRowDto): SrAttachment {
  return {
    key: `att-srv-${r.oid}`, oid: r.oid,
    name: r.name ?? '', cat: String(r.category ?? 5),
    file: r.fileName ?? '', fileDataOid: r.fileDataOid ?? '',
    date: r.date ?? '', op: r.operator ?? '', opOid: r.operatorOid ?? '',
    recv: r.receivedDate ?? '', comment: r.comment ?? '',
  };
}

interface ImageRowDto {
  oid: string;
  name: string;
  fileDataOid: string | null;
  comment: string;
  sort: number;
  fileSize: number | null;
}

function mapImageRow(r: ImageRowDto): SrImage {
  return {
    key: `img-srv-${r.oid}`, oid: r.oid,
    name: r.name ?? '', photo: '', fileDataOid: r.fileDataOid ?? '',
    sort: r.sort ?? 0, comment: r.comment ?? '',
  };
}

export interface SampleRegistrationDetailPageProps {
  /** null = New; a Job ID = Edit. */
  jobId: string | null;
  /** Registration Type chosen in the New dialog ('Regular' | 'Pre-scheduled'). */
  registrationType: string;
  /** Optional list-row values shown while the detail GET is loading. */
  seed?: { client: string; projectName: string; status: string; receivedDate: string | null; dueDate: string | null };
  /** Ordered Job IDs of the current list page — powers the prev/next arrows. */
  siblingIds?: string[];
  /** Current user (Created By / Received By / author). */
  user: string;
  onHome: () => void;
  /** Cancel / Save and Close → back to the list. */
  onClose: () => void;
  /** Jump to another record (prev/next arrows). */
  onNavigate: (jobId: string) => void;
  /** Refresh the list behind the detail after a save or a delete. */
  onSaved: () => void;
  /**
   * Workflow step to open on (0 Registration … 5 Submit). Defaults to 0.
   *
   * Set when another form sends the user HERE to do something that lives on a
   * later step — Sample Transfer's Test Assignment routes Add / Delete to the
   * Samples step, where test assignment is actually edited. Landing on step 0
   * would leave the operator to find their own way there.
   */
  initialStep?: StepIndex;
}

export default function SampleRegistrationDetailPage({
  jobId, registrationType, seed, siblingIds = [], user,
  onHome, onClose, onNavigate, onSaved, initialStep = 0,
}: SampleRegistrationDetailPageProps) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();

  // ── State ─────────────────────────────────────────────────────────────────
  // Server-local "now" — the form stamps drafts with it (§14b.6: while the UTC
  // kill-switch is on, dates are saved and shown as the server's wall clock).
  const nowLocal = useMemo(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  const [currentJobId, setCurrentJobId] = useState<string | null>(jobId);
  const [jobOid, setJobOid] = useState<string | null>(null);
  // COC "Import Pre-Log" matrix handoff (legacy parity, user report 2026-09-24):
  // legacy does NOT ask for a Sample Matrix before Import — the matrix comes from
  // the chosen pre-log (SampleRegistrationViewController.btncocImporting_Accepting
  // → `objsamplecheckin.SampleMatries = objCOCSettings.SampleMatries`). AI splits
  // save + import and the save requires a matrix, so we inject the chosen
  // pre-log's matrix into the auto-save via this ref, and read the just-saved job
  // Oid back for the import POST — no user prompt.
  const matrixOverrideRef = useRef<string[] | null>(null);
  const lastSavedOidRef = useRef<string | null>(null);
  // True once the saved job has carried samples (imported, generated or loaded). A
  // job whose samples are then ALL deleted must stay empty — regenerating the default
  // 1 sample + 1 test on the next Samples click contradicted the delete (bug 5,
  // 2026-10-16). Only a saved job that never had samples is seeded from No Of Samples.
  const jobHadSamplesRef = useRef(false);
  // True once the user has EXPLICITLY Saved / Submitted this session (set in the
  // shared save's onSaved). A NEW registration is auto-persisted just to enter the
  // Samples/Tests/Containers steps (the server mints SampleNo), so on Cancel we
  // must discard that auto-created job unless the user actually Saved it — legacy
  // XAF rolls the uncommitted ObjectSpace back, leaving no JobID (user report
  // 2026-09-30: "New → add details → Cancel still left the JobID saved").
  const explicitlySavedRef = useRef(false);
  // The "Test must be assigned to the sample." warning is shown once per open.
  const noTestWarnedRef = useRef(false);
  // Received Dates are only authoritative for the save once loaded. A NEW job
  // has nothing to load (sub-tab starts empty), so it's "loaded" immediately;
  // an EXISTING job stays false until its SC_ReceivedDates are fetched, so a
  // premature save can't wipe them (persist sends null until loaded).
  const [rdLoaded, setRdLoaded] = useState<boolean>(!jobId);
  const [notesLoaded, setNotesLoaded] = useState<boolean>(!jobId);
  const [sccLoaded, setSccLoaded] = useState<boolean>(!jobId);
  const [icLoaded, setIcLoaded] = useState<boolean>(!jobId);
  const [attLoaded, setAttLoaded] = useState<boolean>(!jobId);
  const [imgLoaded, setImgLoaded] = useState<boolean>(!jobId);
  const [step, setStep] = useState<StepIndex>(initialStep);
  const [slTab, setSlTab] = useState<SlTab>('login');
  const [header, setHeader] = useState<SrHeader>(() => seedHeader(jobId, registrationType, nowLocal, user, seed));
  const [samples, setSamples] = useState<SrSample[]>([]);
  const [active, setActive] = useState(0);
  const [tabs, setTabs] = useState<SrTabState>(() => seedTabs(nowLocal, user));
  const [customRequest, setCustomRequest] = useState<Record<string, { hold: boolean; subOut: boolean; due: string; tat: string }>>({});
  const [lookups, setLookups] = useState<Record<SrLookupKey, SrLookup>>(() => cloneLookups());
  /** Monotonic id of the newest lookups request — see loadLookups' race guard. */
  const lookupSeqRef = useRef(0);
  const [groupChildren, setGroupChildren] = useState<Record<string, { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string }[]>>({});
  // Duplicate-parameter justifications captured in the Test Assignment popup
  // (legacy ValidateDuplicateAnalysis → Reason). Sent with the page Save so each
  // reason is audited (§14a Rule A), and used to skip the page-save re-prompt.
  const [dupJustifications, setDupJustifications] = useState<
    { sampleKey: string; paramName: string; testNames: string[]; reason: string }[]>([]);
  // Job-level set of parameter NAMES already justified as duplicates (ALPACA
  // ValidateDuplicateAnalysis parity — justify ONCE per parameter per job). Seeded
  // from the persisted audit rows on open, grown as the user justifies, and NOT
  // cleared on save (unlike the pending dupJustifications), so the alert never
  // re-fires for an already-justified parameter.
  const [justifiedParams, setJustifiedParams] = useState<Set<string>>(() => new Set());
  // The samples AS HYDRATED FROM THE SERVER (the saved set) — used to grandfather
  // duplicates that already exist in saved data so the page-Save duplicate check
  // never re-prompts for them (ALPACA parity; only NEW duplicates are prompted).
  const serverSamplesRef = useRef<SrSample[]>([]);
  const recordDuplicateJustifications = useCallback((
    sampleId: string,
    items: { paramName: string; testNames: string[]; reason: string }[],
  ) => {
    setDupJustifications(prev => {
      // Replace any prior justification for the same sample + parameter, then add.
      const keep = prev.filter(p => !(p.sampleKey === sampleId && items.some(i => i.paramName === p.paramName)));
      return [...keep, ...items.map(i => ({ sampleKey: sampleId, ...i }))];
    });
    setJustifiedParams(prev => {
      const next = new Set(prev);
      for (const i of items) next.add(i.paramName);
      return next;
    });
  }, []);
  // Seed the justified-parameters set from the persisted audit rows when a job is
  // opened. MERGE (union) so an in-session justification is never lost — e.g. after
  // a new job's first save sets jobOid while the fire-and-forget audit write may not
  // have landed yet.
  useEffect(() => {
    if (!jobOid) return;
    let dead = false;
    api.get(`/api/v1/sample-registration/${jobOid}/duplicate-justifications`)
      .then(r => {
        if (dead) return;
        const fromDb = ((r.data as { data?: string[] })?.data ?? []);
        if (fromDb.length) setJustifiedParams(prev => new Set([...prev, ...fromDb]));
      })
      .catch(() => { /* non-critical — in-session set still suppresses re-prompts */ });
    return () => { dead = true; };
  }, [jobOid]);
  const [newLookupKind, setNewLookupKind] = useState<SrLookupKey | null>(null);
  const [newLookupField, setNewLookupField] = useState<keyof SrHeader | null>(null);
  /** A collector just added via the Samples toolbar "Add Collector", handed to
   *  SampleLoginView so it lands in the grid's Collector dropdown + active row. */
  const [pendingGridCollector, setPendingGridCollector] = useState<{ oid: string; name: string } | null>(null);
  const [importKind, setImportKind] = useState<'quote' | 'coc' | null>(null);
  const [reportsOpen, setReportsOpen] = useState(false);
  /** Generic Sample Label popup (legacy `PrintBarcode_DashboardView`). */
  const [labelOpen, setLabelOpen] = useState(false);
  /** Sample Container pH Check popup (legacy `btnPH_Execute`). */
  const [phCheckOpen,   setPhCheckOpen]   = useState(false);
  const [coolerIdOpen,  setCoolerIdOpen]  = useState(false);
  /** Reports ▾ items, loaded from Custom Report Builder (see the effect below). */
  const [reportItems, setReportItems] = useState<FormReportItem[]>([]);
  const [notesOpen, setNotesOpen] = useState(false);
  const [triedSave, setTriedSave] = useState(false);
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [tatHoursMap, setTatHoursMap] = useState<Record<string, number>>({});
  const [holidaySet, setHolidaySet] = useState<ReadonlySet<string>>(new Set());
  const [statusNameMap, setStatusNameMap] = useState<Record<string, string>>({});
  // Bumped after "Import Pre-Log" replaces the job's header/samples/tests server-
  // side — forces the detail-load effect below to re-fetch even though `jobId`
  // itself did not change.
  const [reloadTick, setReloadTick] = useState(0);

  const isNew = currentJobId === null;

  // ── Module Access Control (Role-Based MAC) — ALPACA parity ────────────────
  // Legacy SampleRegistrationViewController resolves SampleRegIsCreate / IsWrite /
  // IsDelete from the user's role permissions on the Samplecheckin nav item:
  //   Read   → view only (no New / Edit / Save / Delete)
  //   Write  → Edit + Save (and the Submit / Save As-less edit actions)
  //   Create → New + Save of a NEW record
  //   Delete → Delete only
  // Admins fall open (useNavActions). A record opened via New is governed by Create
  // (a Create-only user can finish and save what they just started); an existing
  // record needs Write to be edited, otherwise it opens read-only.
  const perm = useNavActions('sampreg');
  const openedAsNew = !jobId;
  const canSave = openedAsNew ? perm.create : perm.write;
  const canDeleteJob = perm.delete;
  const canSaveAsJob = perm.create;
  const readOnly = !canSave;
  const patch = useCallback((p: Partial<SrHeader>) => {
    dirtyRef.current = true;
    setHeader(h => ({ ...h, ...p }));
  }, []);

  // ── Dirty-form guard ────────────────────────────────────────────────────
  // Tracks whether the user has modified the form since load.  Any `patch()`
  // call sets it; loading from API or resetting clears it.
  const dirtyRef = useRef(false);
  // ── Received-date auto-seed guard ───────────────────────────────────────
  // Fires once per new-job session (XAF parity: one receipt event created
  // automatically from the header when the user first picks a matrix).

  // Registered with the SHARED guard (hooks/useUnsavedGuard.ts), which also covers
  // sidebar navigation, Sign Out and the browser refresh/close prompt — this
  // page used to keep a private copy of the prompt that only its own buttons saw.
  // Read through the ref (not state) so a keystroke never re-renders this form.
  // A NEW job auto-persisted only to enter the Samples/Tests steps is still an
  // unsaved registration (G5: the new object lives in an uncommitted ObjectSpace
  // until Save). Leaving it — sidebar, Sign Out, breadcrumb — and answering OK to
  // "Unsaved changes will be lost" must therefore REMOVE that draft, not keep it
  // (Bug 3: the Job ID used to survive as "Pending Submission").
  const hasAbandonableDraft = useCallback(
    () => !jobId && !explicitlySavedRef.current && !!(jobOid ?? lastSavedOidRef.current),
    [jobId, jobOid]);
  const hasAbandonableDraftRef = useRef(hasAbandonableDraft);
  hasAbandonableDraftRef.current = hasAbandonableDraft;
  const discardDraftRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => registerUnsavedGuard(
    () => dirtyRef.current || hasAbandonableDraftRef.current(),
    () => discardDraftRef.current(),
  ), []);

  const guardedExit = useCallback(async (action: () => void) => {
    if (await confirmDiscardIfUnsaved()) { dirtyRef.current = false; action(); }
  }, []);

  // Cancel / return-to-list from a NEW registration that was auto-persisted (to
  // enter the Samples steps) but never explicitly Saved → soft-delete the orphan
  // job so no JobID is left behind (legacy XAF discards the uncommitted
  // ObjectSpace on Cancel). Only fires for a job opened as NEW (jobId prop null);
  // an existing job, or one the user actually Saved, is never touched. The server
  // refuses if any sample already has real data (QC batch / SDMS / result).
  const discardIfAbandonedNewJob = useCallback(async () => {
    if (jobId) return;                       // opened as an existing job — never discard
    if (explicitlySavedRef.current) return;  // user explicitly Saved — keep it
    const oid = jobOid ?? lastSavedOidRef.current;
    if (!oid) return;                        // nothing was persisted — nothing to discard
    lastSavedOidRef.current = null;          // one discard per draft (leave guard + Cancel can both call)
    try {
      await api.post(`/api/v1/sample-registration/${encodeURIComponent(oid)}/discard`);
    } catch { /* non-fatal: an orphan draft is better than blocking the exit */ }
  }, [jobId, jobOid]);
  discardDraftRef.current = discardIfAbandonedNewJob;

  // Guarded exit that ALSO discards an abandoned new job. Used by Cancel and the
  // return-to-list / Home breadcrumbs — NOT by Refresh or prev/next (those keep
  // the plain guardedExit, which must not delete the job it is about to reload).
  const guardedCancel = useCallback(async (action: () => void) => {
    if (!(await confirmDiscardIfUnsaved())) return;
    dirtyRef.current = false;
    await discardIfAbandonedNewJob();
    action();
  }, [discardIfAbandonedNewJob]);

  useEffect(() => {
    dirtyRef.current = false;
    explicitlySavedRef.current = false;
    noTestWarnedRef.current = false;
    setCurrentJobId(jobId);
    // Reset jobOid too — otherwise navigating from an existing job straight to
    // a NEW one (prev/next arrows, opening "New" without unmounting) leaves it
    // pointing at the PREVIOUS job, which makes savedJob wrongly true on a
    // genuinely new draft (found 2026-09-01: this disabled "Import Pre-Log" on
    // brand-new records, which must stay enabled until the job is actually
    // saved). Re-populated by the readDetail effect below when jobId is real.
    setJobOid(null);
    setStep(0);
    setSlTab('login');
    setSamples([]);
    setActive(0);
    setTabs(seedTabs(nowLocal, user));
    setCustomRequest({});
    setTriedSave(false);
    setFlash(new Set());
    setDetailError(null);
    setHeader(seedHeader(jobId, registrationType, nowLocal, user, seed));
    // Reset loaded flags — new job = nothing to load (true), existing = wait for GET (false)
    const isNewJob = !jobId;
    setRdLoaded(isNewJob);
    setNotesLoaded(isNewJob);
    setSccLoaded(isNewJob);
    setIcLoaded(isNewJob);
    setAttLoaded(isNewJob);
    setImgLoaded(isNewJob);
  }, [jobId, registrationType, nowLocal, user, seed]);

  // ── Received Dates: NOT auto-created before save (G5 parity, user request
  // 2026-09-26). Legacy creates the SC_ReceivedDates row in ObjectSpace_Committing
  // (on SAVE), NOT when a matrix is picked — the earlier "auto-seed on matrix"
  // effect was WRONG. The row is now synthesised from the header at SAVE time
  // (see receivedDatesBody below) so it appears only after saving, like G5.

  // ── "Received By" data source — the GLOBAL employee lookup ────────────────
  // Loaded from GET /api/v1/lookups/employees (dbo.AI_Employee_Lookup_SP), NOT
  // from this form's own lookups SP: one roster, shared by every form that needs
  // an employee picker (CLAUDE.md §14b "don't reinvent").  Fetched once per
  // mount — it must NOT re-fetch when the matrix changes, unlike the SR lookups.
  //
  // ONE column: Employee.DisplayName straight from the table, in the SP's own
  // ASC order (no client-side re-sort).  Rows whose DisplayName is blank are
  // dropped — they would render as an unpickable empty line.
  const [employeeRows, setEmployeeRows] = useState<string[][]>([]);
  const [employeeOidByLabel, setEmployeeOidByLabel] = useState<Record<string, string>>({});
  useEffect(() => {
    const ac = new AbortController();
    void fetchEmployeeLookup(undefined, ac.signal)
      .then(list => {
        const rows: string[][] = [];
        const oidMap: Record<string, string> = {};
        for (const e of list) {
          const name = e.displayName.trim();
          if (!name) continue;
          rows.push([name]);
          if (e.oid) oidMap[name] = e.oid;
        }
        setEmployeeRows(rows);
        setEmployeeOidByLabel(oidMap);
      })
      .catch(() => {
        // Non-fatal: the picker stays empty and the field remains free-text.
        // The server already logged the failure (ExceptionLog middleware).
      });
    return () => ac.abort();
  }, []);

  // ── Rule data (TAT hours + holidays) for due-date calculation ──────────────
  useEffect(() => {
    const ac = new AbortController();
    void api.get('/api/v1/sample-registration/rule-data', { signal: ac.signal })
      .then(res => {
        const d = (res.data as ApiSuccess<{ tatEntries: { tatName: string; hours: number; isDefault: boolean }[]; holidays: string[]; statuses: { statusId: string; statusName: string }[] }>).data;
        const map: Record<string, number> = {};
        for (const e of d.tatEntries) map[e.tatName] = e.hours;
        setTatHoursMap(map);
        setHolidaySet(new Set(d.holidays));
        // Status ID → display name map for the inactivation state machine
        const smap: Record<string, string> = {};
        for (const s of (d.statuses ?? [])) smap[s.statusId] = s.statusName;
        setStatusNameMap(smap);
        // G5 parity (user request 2026-09-26): the default TAT is pre-filled ONLY
        // for REGULAR jobs — Samplecheckin.AfterConstruction gates it on
        // `!IsSampling`. Pre-scheduled jobs keep TAT = N/A.
        const def = d.tatEntries.find(e => e.isDefault);
        const isPre = /^pre-?scheduled$/i.test(registrationType);
        if (def && !header.tat && !isPre) setHeader(h => ({ ...h, tat: def.tatName }));
      })
      .catch(() => { /* non-fatal: due-date auto-calc won't fire */ });
    return () => ac.abort();
  }, []);

  /** Lookups handed to the form: the SR catalogs plus the global employee roster.
   *  Every lookup carries an oidByLabel map (populated from Col5 of the SP for
   *  the SR catalogs, from EmployeeLookupItem.oid for the global roster) so
   *  persist() can resolve display → FK Oid at Save time. */
  const formLookups = useMemo<Record<SrLookupKey, SrLookup>>(
    () => ({
      ...lookups,
      employee: {
        cols: lookups.employee.cols,
        rows: employeeRows,
        oidByLabel: employeeOidByLabel,
      },
    }),
    [lookups, employeeRows, employeeOidByLabel]);

  const loadLookups = useCallback(async (matrixName?: string, clientName?: string, clientOid?: string) => {
    const params: Record<string, string> = {};
    if (matrixName) params.matrixName = matrixName;
    if (clientName) params.clientName = clientName;
    // Prefer the exact client Oid — unambiguous when client names collide.
    if (clientOid) params.clientOid = clientOid;
    // Race guard: this effect fires twice on open — first with the seeded client
    // and NO matrix (the detail GET has not landed yet), then again with the job's
    // Sample Matrix. The no-matrix call returns EVERY matrix's tests and is the
    // slower of the two, so without this guard its late response overwrote the
    // matrix-scoped one and the Test Assignment "Available Test" grid listed tests
    // from other matrices (194 rows instead of the 190 of Drinking Water — ALPACA
    // scopes strictly by the sample's matrix, SampleLogInViewController's "Filter"
    // criteria). Only the NEWEST request may publish (user report 2026-10-03).
    const seq = ++lookupSeqRef.current;
    const res = await api.get('/api/v1/sample-registration/lookups', {
      params: Object.keys(params).length ? params : undefined,
    });
    if (seq !== lookupSeqRef.current) return;
    const payload = (res.data as ApiSuccess<SampleRegistrationLookupRowDto[]>).data ?? [];
    setLookups(mapLookupRows(payload));
    const gc: Record<string, { name: string; method: string; comp: string; isFieldTest?: boolean; oid?: string }[]> = {};
    for (const row of payload) {
      if (row.lookupKey !== 'testgroup') continue;
      const grpName = row.col1 ?? '';
      if (!grpName) continue;
      (gc[grpName] ??= []).push({ name: row.col2 ?? '', method: row.col3 ?? '', comp: row.col4 ?? '', isFieldTest: row.col6 === '1' || undefined, oid: row.col5 ?? '' });
    }
    setGroupChildren(gc);
  }, []);

  useEffect(() => {
    let dead = false;
    const readDetail = async () => {
      // Reload by the CURRENT job id, not the immutable `jobId` PROP. A COC
      // "Import Pre-Log" on a brand-new registration mints the job AFTER mount,
      // so `jobId` stays null while `currentJobId` holds the minted id. Keying
      // the fetch on `currentJobId` lets the post-import reload (setReloadTick)
      // actually refresh the whole header — COC ID, TAT, Sample Matrix and the
      // rest — matching G5, which reloads the entire Samplecheckin after Import.
      // (currentJobId is intentionally NOT an effect trigger — see the dep note
      // below — so the normal save/navigation flows that also set it don't force
      // an extra header reload; it is read fresh only when reloadTick/jobId fire.)
      const loadId = currentJobId ?? jobId;
      if (!loadId) {
        setDetailError(null);
        return;
      }
      setDetailLoading(true);
      setDetailError(null);
      try {
        const res = await api.get(`/api/v1/sample-registration/${encodeURIComponent(loadId)}`);
        const dto = (res.data as ApiSuccess<SampleRegistrationDetailDto>).data;
        if (dead) return;
        if (dto.oid) setJobOid(dto.oid);
        setHeader(mapDetailToHeader(dto, registrationType, nowLocal, user));
        dirtyRef.current = false;

        // Load all child tab data so they can be edited/saved.
        if (dto.oid) {
          const oidEnc = encodeURIComponent(dto.oid);
          const loadChild = async <T,>(
            path: string,
            map: (r: T) => unknown,
            tabKey: keyof SrTabState,
            setLoaded: (v: boolean) => void,
            isSingle?: boolean,
          ) => {
            try {
              const res = await api.get(`/api/v1/sample-registration/${oidEnc}/${path}`);
              if (dead) return;
              const raw = (res.data as ApiSuccess<T | T[]>).data;
              if (isSingle) {
                const mapped = raw ? [map(raw as T)] : [];
                setTabs(t => ({ ...t, [tabKey]: mapped }));
              } else {
                const arr = (raw as T[] | null) ?? [];
                setTabs(t => ({ ...t, [tabKey]: arr.map(map) }));
              }
            } catch {
              /* non-fatal: the sub-tab stays empty; server logged the failure */
            } finally {
              if (!dead) setLoaded(true);
            }
          };

          // Load Custom Request overrides so the tab restores on reopen.
          const loadCustomRequests = async () => {
            try {
              const res = await api.get(`/api/v1/sample-registration/${oidEnc}/custom-requests`);
              if (dead) return;
              const rows = ((res.data as ApiSuccess<CustomRequestRowDto[]>).data) ?? [];
              if (rows.length > 0) {
                const map: Record<string, { hold: boolean; subOut: boolean; due: string; tat: string }> = {};
                for (const r of rows) {
                  map[r.testName] = {
                    hold:   r.hold,
                    subOut: r.subOut ?? false,
                    // Legacy "no date" sentinel (1899/1900-01-01 / 0001-01-01) means unset —
                    // leave empty so the grid falls back to the registration's Due Date.
                    due:    r.dueDate && !/^(0001|18\d\d|1900)-/.test(r.dueDate) ? r.dueDate : '',
                    tat:    r.tatName ?? '',
                  };
                }
                setCustomRequest(map);
              }
            } catch {
              /* non-fatal */
            }
          };

          // Load persisted SampleLogIn + SampleParameter rows so Edit Mode restores
          // the top and bottom grids from the DB instead of re-generating from noOfSamples.
          const loadSampleLogins = async () => {
            try {
              const res = await api.get(`/api/v1/sample-registration/${oidEnc}/sample-logins`);
              if (dead) return;
              const payload = (res.data as ApiSuccess<SampleLoginsDto | null>).data;
              const mapped = mapSampleLoginsPayload(payload);
              serverSamplesRef.current = mapped;   // saved baseline for duplicate grandfathering
              // Reflect the server EXACTLY — including an empty result after every
              // sample on the job was deleted. The previous `mapped.length > 0` guard
              // left the just-deleted rows on screen (the delete committed, the grid
              // did not refresh — user report 2026-10-01). This runs only for a
              // PERSISTED job (dto.oid present), so the server is authoritative.
              setSamples(mapped);
              // G5 parity (SampleRegistrationViewController.EditAction_Execute):
              // opening a saved job for edit warns when ANY of its samples has no
              // test. Once per open — not on the reloads that follow a save/delete.
              if (jobId && !noTestWarnedRef.current && mapped.some(s => s.tests.length === 0)) {
                noTestWarnedRef.current = true;
                notify.warning('Test must be assigned to the sample.');
              }
              if (mapped.length > 0) {
                // G5 parity: the header "No Of Samples" shows the ACTUAL loaded /
                // imported sample count. Samplecheckin.NumberOfSample is unreliable
                // (a COC pre-log imports 0 there), so legacy shows the real
                // SampleLogIn count — not the stored column and not the "1" default.
                setHeader(h => ({ ...h, noOfSamples: String(mapped.length) }));
              }
            } catch {
              /* non-fatal — sample grids stay empty; user can still edit */
            }
          };

          await Promise.all([
            loadChild<ReceivedDateRowDto>('received-dates', mapReceivedDateRow, 'receivedDates', setRdLoaded),
            loadChild<NoteRowDto>('notes', mapNoteRow, 'notes', setNotesLoaded),
            loadChild<SccDto>('scc', mapSccRow as (r: SccDto) => unknown, 'sccRecords', setSccLoaded),
            loadChild<ItemChargeRowDto>('item-charges', mapItemChargeRow, 'itemCharges', setIcLoaded),
            loadChild<AttachmentRowDto>('attachments', mapAttachmentRow, 'attachments', setAttLoaded),
            loadChild<ImageRowDto>('images', mapImageRow, 'images', setImgLoaded),
            loadCustomRequests(),
            loadSampleLogins(),
          ]);
        } else {
          setRdLoaded(true);
          setNotesLoaded(true);
          setSccLoaded(true);
          setIcLoaded(true);
          setAttLoaded(true);
          setImgLoaded(true);
        }
      } catch (e) {
        if (dead) return;
        const msg = e instanceof Error ? e.message : 'Failed to load registration.';
        setDetailError(msg);
        notify.error('Failed to load Sample Registration detail.');
      } finally {
        if (!dead) {
          setDetailLoading(false);
          // A (re)load replaces the form with what the server holds, so it is clean.
          // The shared tracker (hooks/useUnsavedGuard) also counts the clicks the
          // user made inside the Import Pre-Log / Quote dialog as "edits" — without
          // this the sidebar showed "Unsaved changes will be lost" right after an
          // import that had already been committed server-side.
          dirtyRef.current = false;
          resetUnsavedTracking();
        }
      }
    };
    void readDetail();
    return () => { dead = true; };
    // currentJobId is read fresh inside readDetail but deliberately omitted from
    // the triggers (it also changes on save/step-nav, which must NOT force a
    // header reload); the reload fires only on jobId / reloadTick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, notify, nowLocal, registrationType, user, reloadTick]);

  // Lookups reload when the Sample Matrix (gates Test) OR the Client changes —
  // project / contact / quote / collector are CLIENT-SCOPED server-side, so the
  // lists are simply empty until a client is chosen (legacy `1=2`).
  useEffect(() => {
    void loadLookups(header.matrices[0], header.client, header.clientOid);
  }, [header.matrices, header.client, header.clientOid, loadLookups]);

  // Due Date = Received Date + TAT hours (working days/hours, skipping
  // weekends + holidays).  Auto-calculates ONLY when TAT / Received Date /
  // Registration Type actually CHANGE — then the user can manually adjust the
  // editable datetime field (type or ↑/↓ on a segment) and it STICKS.
  //
  // The recompute must NOT depend on `header.dueDate`: an earlier version did, so
  // every manual edit re-ran the effect, recomputed the same Received+TAT value,
  // saw it differ from the user's value, and snapped it straight back — the date
  // field's arrows/typing appeared dead (user report 2026-09-21). A signature ref
  // of the *inputs* gates the recompute so it fires on a genuine input change
  // only, never on a manual dueDate edit (and never loops if patch / the lookup
  // maps get a fresh identity on re-render).
  const dueAutoSigRef = useRef<string>('');
  useEffect(() => {
    const hours = tatHoursMap[header.tat];
    if (hours === undefined) return;
    const sig = `${header.receivedDate}|${header.tat}|${registrationType}`;
    if (sig === dueAutoSigRef.current) return;   // inputs unchanged → keep any manual edit
    dueAutoSigRef.current = sig;
    // G5 parity (user request 2026-09-26): a PRE-SCHEDULED job has no Received
    // Date at registration, so legacy leaves Due Date EMPTY (it is computed later
    // from the real received date). The registrationType prop is 'Regular' |
    // 'Pre-scheduled' (see props, line 431) — the old check compared against
    // 'Sampling', which never matched, so isSampling stayed false and Due Date
    // wrongly auto-filled from receivedDate=now. resolveDueDate returns '' when
    // isSampling is true, so a correct check leaves pre-scheduled Due Date empty
    // (and never wipes a real due date loaded on an existing job, since '' is not
    // patched).
    const isSampling = registrationType === 'Pre-scheduled';
    const due = resolveDueDate(header.receivedDate, hours, isSampling, holidaySet);
    // SILENT update (setHeader, not patch): Due Date here is DERIVED from TAT +
    // Received Date, so auto-filling it must NOT mark the form dirty — otherwise a
    // freshly loaded/imported/saved job is "dirty" the instant this effect runs and
    // Cancel wrongly prompts "Unsaved changes will be lost". A real user edit to
    // TAT still marks the form dirty via the TAT field's own onChange.
    if (due) setHeader(h => (h.dueDate === due ? h : { ...h, dueDate: due }));
  }, [header.tat, header.receivedDate, tatHoursMap, holidaySet, registrationType]);

  // Clearing the Sample Matrix re-gates (and clears) Test + No Of Samples.
  // SILENT + guarded: only fires when there is actually something to reset, and
  // via setHeader (not patch) so it never marks a freshly-loaded/new form dirty
  // (the matrix removal that triggers it is the user's edit and already dirties
  // the form; this is just its cascade).
  useEffect(() => {
    if (header.matrices.length > 0) return;
    if (header.testTemplate.length > 0 || header.noOfSamples !== '1') {
      setHeader(h => ({ ...h, testTemplate: [], noOfSamples: '1' }));
    }
  }, [header.matrices, header.testTemplate.length, header.noOfSamples]);

  const matrixMap = useMemo(() => {
    const m: Record<string, string> = {};
    const obl = formLookups.matrix?.oidByLabel;
    if (obl) {
      for (const [name, oid] of Object.entries(obl)) {
        // Oid → name (lowercase — .NET Guid.ToString() returns lowercase)
        m[oid.toLowerCase()] = name;
        // name → name (new samples store the display name, not the Oid)
        m[name] = name;
      }
    }
    return m;
  }, [formLookups.matrix]);

  // Collector OID → name (same shape as matrixMap) so the Parameters tab shows
  // the field-test AnalyzedBy as the collector's NAME, not the raw Oid/GUID
  // (user report 2026-09-23).
  const collectorMap = useMemo(() => {
    const m: Record<string, string> = {};
    const obl = formLookups.collector?.oidByLabel;
    if (obl) {
      for (const [name, oid] of Object.entries(obl)) {
        m[oid.toLowerCase()] = name;   // Oid → name (.NET Guid.ToString() is lowercase)
        m[name] = name;                // name → name (new samples may store the name)
      }
    }
    return m;
  }, [formLookups.collector]);

  const unitNames = useMemo(
    () => (formLookups.unit?.rows ?? []).map(r => r[0]).filter((v): v is string => !!v),
    [formLookups.unit],
  );

  // Employee display names (global roster) for the Parameters-tab AnalyzedBy
  // dropdown (#9). Drops any raw-Guid placeholder names the roster may carry.
  const employeeNames = useMemo(
    () => (formLookups.employee?.rows ?? [])
      .map(r => r[0])
      .filter((v): v is string => !!v && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v)),
    [formLookups.employee],
  );

  /** The header-level test template as test records (Custom Request rows).
   *  testTemplate entries are the "TestName|MethodNumber" composite — split back
   *  to the bare name + method so the samples/tests grid shows them cleanly. */
  // The header "Test" selection → the per-sample test template. ALPACA parity
  // (SampleRegistrationViewController, confirmed against the legacy source
  // 2026-09-30):
  //   • a picked TEST adds ONLY its Default component — never the non-default
  //     components (e.g. Alkalinity "watershed"). The test lookup returns one row
  //     PER component, so we must select the `Default` row, not the first
  //     (user report: "Alkalinity shows all components instead of only Default").
  //   • a GROUP test (col3 = '1') is EXPANDED into its child tests via
  //     GroupTestMethod (groupChildren), each child added as its own test — a group
  //     row itself is not a runnable test (user report: "group test not shown").
  //   • duplicate composites collapse to one test (a per-component lookup can
  //     otherwise yield the same test+method twice).
  const templateTests = useMemo(() => {
    const out: SrTest[] = [];
    const seenComposite = new Set<string>();
    const pushDedup = (t: SrTest, dedupKey: string, keySet: Set<string>) => {
      if (keySet.has(dedupKey)) return;
      keySet.add(dedupKey);
      out.push(t);
    };
    const childKeys = new Set<string>();
    for (const composite of header.testTemplate) {
      if (seenComposite.has(composite)) continue;
      seenComposite.add(composite);
      const bar = composite.indexOf('|');
      const name = bar >= 0 ? composite.slice(0, bar) : composite;
      const method = header.testTemplateMethods[composite] ?? (bar >= 0 ? composite.slice(bar + 1) : '');
      const rows = lookups.test.rows.filter(r => r[0] === name && r[1] === method);
      const anyRow = rows[0] ?? lookups.test.rows.find(r => r[0] === name);
      const isGroup = anyRow?.[2] === '1';
      if (isGroup) {
        // Expand the group into its child tests, each at its Default component.
        const children = groupChildren[name] ?? [];
        const seenChild = new Set<string>();
        for (const ch of children) {
          const ck = `${ch.name}|${ch.method}`;
          // Prefer the Default child row; skip a non-default duplicate of one seen.
          if ((ch.comp || 'Default') !== 'Default' && seenChild.has(ck)) continue;
          seenChild.add(ck);
          pushDedup(
            mkTest(ch.name, ch.method, 'Default', '', name, ch.isFieldTest, ch.oid || undefined),
            ck, childKeys,
          );
        }
        continue;
      }
      // Non-group: add ONLY the Default component of this test+method.
      const defRow = rows.find(r => (r[3] || 'Default') === 'Default') ?? rows[0] ?? anyRow;
      const oid = defRow?.[5];
      const isFieldTest = defRow?.[4] === '1';
      pushDedup(
        mkTest(name, method, 'Default', '', '', isFieldTest, oid || undefined),
        `${name}|${method}`, childKeys,
      );
    }
    return out;
  }, [header.testTemplate, header.testTemplateMethods, lookups.test.rows, groupChildren]);

  // Bug 12: deleting a test (Tests tab / Test Edit) left it in the header Test field —
  // and so in the Custom Request rows and on the next Save. When a test that WAS on
  // the samples is no longer on any of them, drop it from the header template too.
  // Only tests seen on the samples before are touched, so a test the user has just
  // picked in the header (not yet on any sample) is never removed by mistake.
  const prevSampleTestKeysRef = useRef<Set<string>>(new Set());
  const prevSampleCountRef = useRef(0);
  useEffect(() => {
    // Deleting a SAMPLE (fewer rows, or none left) removes its tests with it, but the
    // header Test is the registration's own choice and must stay (bug 6, 2026-10-16);
    // only a TEST deletion on the surviving samples prunes the header.
    const sampleCountDropped = samples.length < prevSampleCountRef.current;
    prevSampleCountRef.current = samples.length;
    const cur = new Set<string>();
    for (const s of samples) for (const t of s.tests) {
      cur.add(`${t.name}|${t.method}`);
      if (t.testGroupName) cur.add(`grp:${t.testGroupName}`);
    }
    const prev = prevSampleTestKeysRef.current;
    prevSampleTestKeysRef.current = cur;
    if (sampleCountDropped || samples.length === 0) return;
    if (prev.size === 0 || header.testTemplate.length === 0) return;
    const gone = (c: string) => {
      const bar = c.indexOf('|');
      const name = bar >= 0 ? c.slice(0, bar) : c;
      const key = `${name}|${header.testTemplateMethods[c] ?? (bar >= 0 ? c.slice(bar + 1) : '')}`;
      const seenBefore = prev.has(key) || prev.has(`grp:${name}`);
      return seenBefore && !cur.has(key) && !cur.has(`grp:${name}`);
    };
    if (!header.testTemplate.some(gone)) return;
    setHeader(h => ({ ...h, testTemplate: h.testTemplate.filter(c => !gone(c)) }));
  }, [samples]);   // eslint-disable-line react-hooks/exhaustive-deps -- reacts to sample edits only

  // ── Counts driving the stepper + flow bar ────────────────────────────────
  const counts = useMemo(() => {
    if (samples.length > 0) {
      return {
        samples: samples.length,
        tests: samples.reduce((a, s) => a + s.tests.length, 0),
        containers: samples.reduce((a, s) => a + (Number.parseInt(s.v.Qty ?? '1', 10) || 1), 0),
      };
    }
    // G5 parity (user request 2026-09-26): legacy shows Samples(0)/Tests(0)/
    // Containers(0) until samples actually exist — it does NOT project a count
    // from "No Of Samples" before save. So no preview: the counts stay 0 until
    // real sample rows are generated (on entering the Samples step) or loaded
    // after save. Removed the old `No Of Samples × test template` preview that
    // made the flow bar read e.g. 2/8/2 before anything was created.
    return { samples: 0, tests: 0, containers: 0 };
  }, [samples]);

  // Flow-bar chip counts mirror G5 EXACTLY (user report). Legacy reads
  // "Samples(57) Tests(4) Containers(0)" for a 57-sample job with 4 DISTINCT
  // tests and no bottles allocated yet — i.e. Tests = number of DISTINCT tests
  // (not the per-sample total, which would be 57×4 = 228), and Containers =
  // number of ALLOCATED bottles (not the sum of sample Qty, which would be 57).
  // `counts` above keeps the totals it feeds elsewhere (the Import gate + the
  // "Test assignments" summary); only the flow-bar display switches to G5's shape.
  const flowBarCounts = useMemo(() => {
    if (samples.length === 0) return { samples: 0, tests: 0, containers: 0 };
    const testKeys = new Set<string>();
    for (const s of samples) {
      for (const t of s.tests) {
        testKeys.add(`${t.name}|${t.method}|${t.comp}`);
      }
    }
    // Containers — G5 parity (SampleRegistrationViewController btnBottleAllocation
    // caption, :9677 / :21636): the SUM OF Qty over the DISTINCT samples that carry at
    // least one NON-field test; 0 when no sample has a lab test. Computed from the live
    // samples so it updates as soon as a test / Qty changes or a container is saved —
    // the earlier server-allocation count was only fetched when a job was OPENED, so a
    // new job's chip stayed at 0 after containers were added (user report 2026-10-07).
    let containers = 0;
    for (const s of samples) {
      if (s.tests.some(t => !t.isFieldTest)) containers += Number.parseInt(s.v.Qty ?? '', 10) || 0;
    }
    return { samples: samples.length, tests: testKeys.size, containers };
  }, [samples]);

  const submitted = /^submitted/i.test(header.status);

  /** Generic Sample Label — available once the job is SAVED and has a Job ID
   *  (legacy `btnPrintBarcodeLabel` / `UpdatePrintBarcodeLabelActive`). */
  const canPrintBarcodeLabel = !isNew && !!currentJobId;

  /**
   * Reports ▾ — a port of GoLive `FormReportPermissionViewController.OnActivated`.
   * The menu is DATA-DRIVEN from Custom Report Builder (FormViews → Forms →
   * FormReportPermission → tbl_Public_CustomReportDesignerDetails), keeping only
   * Active templates, ordered by Sort, and filtered by the caller's role unless
   * they are an admin. Assigning a template to the form changes this menu with no
   * code change. GoLive HIDES the action when the form has no report, so an empty
   * list hides the button here too.
   */
  useEffect(() => {
    let alive = true;
    api.get('/api/v1/sample-registration/reports')
      .then(res => {
        const list = ((res.data as { data?: FormReportItem[] })?.data ?? [])
          .filter(r => r && r.templateName);
        if (alive) setReportItems(list);
      })
      .catch(() => { if (alive) setReportItems([]); });
    return () => { alive = false; };
  }, []);

  /**
   * Reports ▾ → preview. GoLive `ReportsAction_Execute` requires a source record
   * ("Please select atleast one record to preview the report!"); on this DETAIL
   * view `SetSourceIDForCurrentForm` scopes the report to the current JobID.
   */
  const previewReport = useCallback(async (item: FormReportItem) => {
    setReportsOpen(false);
    if (!currentJobId) {
      notify.warning('Please save the registration before previewing a report.');
      return;
    }
    try {
      const res = await api.get('/api/v1/sample-registration/report-pdf', {
        params: { templateName: item.templateName, jobIds: currentJobId },
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        notify.warning(`${item.label} produced no content for job ${currentJobId}.`);
        return;
      }
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      notify.error(`Failed to preview "${item.label}". The report could not be generated.`);
    }
  }, [currentJobId, notify]);

  /** VisualMatrixName options for the label popup (dbo.VisualMatrix). */
  const labelMatrixOptions = useMemo(
    () => (formLookups.matrix?.rows ?? [])
      .map(r => r[0] ?? '')
      .filter((n): n is string => n.length > 0),
    [formLookups.matrix]);

  /**
   * Legacy prefill fallback — `FindObject<SampleLogIn>("[JobID.Oid] = ? And
   * [VisualMatrix] Is Not Null")`: the first sample of the job that HAS a
   * VisualMatrix. The Sample Login cell holds either the FK Oid (dynamic
   * config path) or the display name (static SL_COLS path), so an Oid is
   * mapped back to its VisualMatrixName before the popup sees it.
   */
  const firstSampleMatrix = useMemo(() => {
    const raw = samples
      .map(s => s.v['SampleMatrix'] || s.v['VisualMatrix'] || '')
      .find(v => v) ?? '';
    if (!raw) return '';
    const byLabel = formLookups.matrix?.oidByLabel ?? {};
    const hit = Object.entries(byLabel)
      .find(([, oidVal]) => (oidVal ?? '').toUpperCase() === raw.toUpperCase());
    return hit ? hit[0] : raw;
  }, [samples, formLookups.matrix]);

  // ── Step state — computed from the DATA, never a static wizard ────────────
  const islandSteps = useMemo((): IslandStep[] => {
    const done = [
      !isNew,                                                                   // Registration saved
      samples.length > 0,                                                       // Samples created
      samples.length > 0 && samples.every(s => s.tests.length > 0),              // every sample has a test
      samples.length > 0 && samples.every(s => s.tests.every(t => !!t.bottle)),  // bottles assigned
      !isNew,                                                                   // record persisted
      submitted,                                                                // submitted
    ];
    // On the Samples step the Sample Login tab decides the active node.
    const activeIdx: number = step === 1
      ? (slTab === 'testview' ? 2 : slTab === 'containers' ? 3 : 1)
      : step;
    return STEPS.map((label, i) => ({
      id: String(i),
      label,
      state: i === activeIdx ? 'active' : done[i] ? 'done' : 'todo',
    }));
  }, [isNew, samples, submitted, step, slTab]);

  // ── Guards ────────────────────────────────────────────────────────────────
  /** Client + Sample Matrix are the only required fields (decision 2026-07-18). */
  const missingRequired = useMemo(() => {
    const out: { label: string; id: string }[] = [];
    if (!header.client) out.push({ label: 'Client', id: 'sr-f-client' });
    const isPrescheduled = /^pre-?scheduled$/i.test((header.registrationType ?? '').trim());
    if (isPrescheduled && !header.dateExpect) out.push({ label: 'Date Expected', id: 'sr-f-date-expect' });
    if (isPrescheduled && !header.projectId) out.push({ label: 'Project ID', id: 'sr-f-project' });
    if (header.matrices.length === 0) out.push({ label: 'Sample Matrix', id: 'sr-f-matrix' });
    return out;
  }, [header.client, header.registrationType, header.dateExpect, header.projectId, header.matrices.length]);

  const warnMissing = useCallback(async (
    message: string, listOverride?: { label: string; id: string }[],
  ) => {
    const list = listOverride ?? missingRequired;
    setTriedSave(true);
    await warnDialog(message, { title: 'Required Fields Missing', list: list.map(m => m.label) });
    // After "OK", flash the offending fields and scroll the first into view so
    // the user can FIND them on the long Registration form.
    setStep(0);
    setFlash(new Set(list.map(m => m.id)));
    setTimeout(() => {
      const first = list[0];
      if (first) document.getElementById(first.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    setTimeout(() => setFlash(new Set()), 4000);
  }, [missingRequired]);

  useEffect(() => {
    if (jobOid && samples.length > 0) jobHadSamplesRef.current = true;
  }, [jobOid, samples]);

  /** Generate the sample rows from No Of Samples (top up, never drop rows).
   *  For saved jobs (jobOid set), the DB-loaded samples are the truth — do NOT
   *  pad extra rows from the registration header's noOfSamples. */
  const ensureSamples = useCallback((job: string) => {
    setSamples(prev => {
      const next = prev.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
      // Only pad from noOfSamples for NEW (unsaved) jobs.  Saved jobs already
      // have their samples loaded from the SampleLogIn table — noOfSamples
      // on the registration header is informational, not prescriptive.
      // ALPACA parity: a SAVED job whose samples were all deleted regenerates its rows
      // from No Of Samples when the Samples step is opened, AND re-assigns the header
      // Test(s) to each — legacy InsertSamplesInSampleLogin runs the same code as for a
      // new job (NPTest → SampleParameter per new sample). Corrected 2026-10-07: an
      // earlier change (2026-10-06) wrongly left the regenerated samples test-less.
      const regenerateBlank = !!jobOid && next.length === 0 && !jobHadSamplesRef.current;
      if (!jobOid || regenerateBlank) {
        const want = Math.max(1, Number.parseInt(header.noOfSamples, 10) || 1);
        // ALPACA parity (user report 2026-10-03): each new sample inherits the
        // header's Collector / Date Collected / Time Collected so they show in the
        // Sample Login grid (editable per sample afterwards). Collector stores the
        // OID (the grid resolves OID→name), resolved from the header's collector name.
        // Resolve to the OID the grid displays; leave EMPTY when the collector
        // lookup hasn't loaded yet (the sync effect below fills it once it has) —
        // never seed a raw name, which the grid's OID→name resolver can't show.
        const collectorName = header.collector[0] ?? '';
        const collectorSeed = collectorName
          ? (formLookups.collector?.oidByLabel?.[collectorName]?.toLowerCase() ?? '')
          : '';
        while (next.length < want) {
          const s: SrSample = {
            id: sampleId(job, next.length + 1),
            hold: false,
            v: {
              Qty: '1',
              SampleMatrix: header.matrices[0] ?? '',
              ReceivedDate: tabs.receivedDates[0]?.oid ?? '',
              Collector: collectorSeed,
              DateCollected: header.dateCollected ?? '',
              TimeCollected: header.timeCollected ?? '',
              EnteredBy: user,
              EnteredDate: toDateInput(nowLocal),
            },
            tests: templateTests.map(cloneTest),
          };
          autoBottles(s);
          next.push(s);
        }
      }
      renumber(next, job);
      return next;
    });
  }, [jobOid, header.noOfSamples, header.matrices, header.receivedDate, header.collector,
      header.dateCollected, header.timeCollected, formLookups.collector,
      tabs.receivedDates, templateTests, user, nowLocal]);

  // ALPACA parity (user report 2026-10-03): the header's Collector / Date Collected
  // / Time Collected default onto each sample's Sample Login grid columns. When the
  // header is set, fill any sample whose own value is still EMPTY — never clobbering
  // a value the user (or the DB) already holds per sample. Runs for EVERY job, not
  // just new ones: a job is PERSISTED early (to enter the Samples tab) so jobOid is
  // set well before the user finishes the header, and a reload's empty columns still
  // want the header default; "fill if empty" keeps a real per-sample value intact.
  // Keyed on samples.length so it also runs once the rows load (async).
  useEffect(() => {
    const collectorName = header.collector[0] ?? '';
    // Resolve the collector NAME → its Collector.Oid (the value the grid's column
    // resolves back to a name). Leave EMPTY when the client-scoped collector lookup
    // hasn't loaded yet — NEVER fall back to the raw name: storing the name here
    // (non-empty but un-displayable by the grid's OID→name resolver) would make the
    // later "fill if empty" pass skip it, so the column stayed blank even once the
    // lookup loaded (user report 2026-10-03). With '' the pass retries and fills the
    // real OID once formLookups.collector arrives.
    // Lowercase to match the grid's config option Oids (Guid.ToString()); the
    // header lookup returns UPPERCASE (SQL CAST), which the grid couldn't match.
    const collectorOid = collectorName
      ? (formLookups.collector?.oidByLabel?.[collectorName]?.toLowerCase() ?? '')
      : '';
    const dc = header.dateCollected ?? '';
    const tc = header.timeCollected ?? '';
    if (!collectorOid && !dc && !tc) return;
    // Task 29935 (ALPACA parity): never default the header's collection info onto an
    // "Uncollected" sample — legacy SampleRegistrationViewController.InsertCheckinData
    // skips them. Case-insensitive: the status OID casing can differ between readers.
    const uncollectedOids = new Set(
      Object.entries(statusNameMap)
        .filter(([, name]) => name === 'Uncollected')
        .map(([oid]) => oid.toLowerCase()),
    );
    const statusMapReady = Object.keys(statusNameMap).length > 0;
    setSamples(prev => {
      let changed = false;
      const next = prev.map(s => {
        const statusOid = (s.v.SampleStatus ?? '').toLowerCase();
        // A sample that already carries a status might be "Uncollected" — never
        // re-fill it until statusNameMap has loaded and confirmed it is NOT (else a
        // reload races the rule-data fetch and re-fills a cleared Uncollected sample,
        // which "fill if empty" could then never undo). A sample with no status yet is
        // new / unprocessed, so defaulting onto it immediately is safe.
        if (statusOid && (!statusMapReady || uncollectedOids.has(statusOid))) return s;
        const v = { ...s.v };
        let touched = false;
        if (collectorOid && !v.Collector)    { v.Collector = collectorOid; touched = true; }
        if (dc && !v.DateCollected)          { v.DateCollected = dc;       touched = true; }
        if (tc && !v.TimeCollected)          { v.TimeCollected = tc;       touched = true; }
        if (touched) { changed = true; return { ...s, v }; }
        return s;
      });
      return changed ? next : prev;
    });
    // header.collector is keyed by its JOINED value, not the array reference — the
    // array is rebuilt on every header edit, which re-ran this effect (and its map
    // over every sample) on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header.collector.join('|'), header.dateCollected, header.timeCollected,
      formLookups.collector, samples.length, statusNameMap]);

  // ── Save / Submit ─────────────────────────────────────────────────────────

  /**
   * The ONE persistence point — POST (create) or PUT (update) to the API.
   * The endpoint owns: the race-safe Job ID (ISequentialIdGenerator), the audit
   * row (IAuditTrailService), the bottle grouping, and the TAT → Due Date derivation.
   */
  const persist = useCallback(async (): Promise<string> => {
    // The header + child collections share ONE atomic Save. Every FK the API
    // parses as a Guid MUST be resolved from display → Oid via `oidByLabel`
    // (populated by mapLookupRows + employee-lookup fetch). Sending a display
    // name like "ACME Labs" for @ClientName makes ParseGuid return NULL, and
    // the SP throws "Client is required" — same for every other FK column.
    // oid() returns null when the map has no entry; callers filter nulls out.
    const oid = (kind: SrLookupKey, label: string | undefined): string | null => {
      if (!label) return null;
      const map = formLookups[kind]?.oidByLabel;
      return map?.[label] ?? null;
    };
    const oidList = (kind: SrLookupKey, labels: readonly string[]): string | null => {
      const xs = labels.map(l => oid(kind, l)).filter((x): x is string => !!x);
      return xs.length ? xs.join('; ') : null;
    };

    // Normalise a datetime-local value so the API reliably keeps the TIME:
    // "YYYY-MM-DDTHH:mm" (16 chars) → append ":00" seconds so System.Text.Json
    // parses the full timestamp; a date-only value is promoted to T00:00:00.
    const wireDateTime = (v: string | undefined): string | null => {
      if (!v) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00`;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:00`;
      return v;
    };

    // Header-level test template as Oids (persisted into SampleCheckinTest).
    const headerTestOids = header.testTemplate
      .map(t => oid('test', t))
      .filter((x): x is string => !!x);

    // Phase 1 child payloads persisted by AI_SampleRegistration_SaveChildren:
    //   · samples[]      → dbo.SampleLogIn
    //   · headerTests[]  → dbo.SampleCheckinTest (M:N junction on the header)
    //   · sampleTests[]  → dbo.SampleParameter (per-sample per-test)
    // `key` is the client-side stable id (SrSample.id / SrTest.key) — server
    // uses it only to join sampleTests to the freshly-minted SampleLogIn.Oid.
    const headerMatrixOid = oid('matrix', header.matrices[0]);
    // Helper: check if a value is already a GUID (stored by the dynamic config
    // FK picker) so we can send it as-is without a display-name→Oid resolution.
    const isGuid = (v?: string | null) =>
      !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

    const samplesBody = samples.map((s, i) => ({
      key:            s.id,
      sampleNo:       i + 1,
      // SampleMatrix: dynamic config stores the OID in v['SampleMatrix'] (the
      // FIELD_ID_TO_V_KEY remapping maps VisualMatrix → SampleMatrix). The static
      // SL_COLS path stores the display name there, so we also check isGuid.
      // Legacy key v['VisualMatrix'] is kept as a final fallback for any old
      // in-flight data that predates the remap.
      matrix:         isGuid(s.v['SampleMatrix']) ? s.v['SampleMatrix']
                    : isGuid(s.v['VisualMatrix'])  ? s.v['VisualMatrix']
                    : headerMatrixOid,
      // SampleType: dynamic config stores the OID directly in v['SampleType'].
      sampleType:     isGuid(s.v['SampleType']) ? s.v['SampleType'] : null,
      qty:            Number(s.v.Qty) || 1,
      // Collector: dynamic config stores the OID in v['Collector'];
      // static fallback stores display name that must be resolved via oid().
      collector:      isGuid(s.v['Collector']) ? s.v['Collector'] : (oid('collector', s.v.Collector) ?? null),
      collectDate:    s.v.DateCollected || s.v.CollectDate || null,
      collectTime:    s.v.TimeCollected || s.v.CollectTimeDisplay || null,
      clientSampleId: s.v.SampleName || s.v.ClientSampleID || null,
      description:    s.v.Description || null,
      comment:        s.v.Remark || null,
      systemType:     isGuid(s.v['SystemType'])   ? s.v['SystemType']   : null,
      qcCategory:     isGuid(s.v['QCCategory'])   ? s.v['QCCategory']   : null,
      hold:           s.hold ?? false,
      subOut:         s.v['SubOut'] === '1' || s.v['SubOut'] === 'true',
      // Additional configurable columns persisted by SaveChildren SP
      samplingLocation: s.v.SamplingLocation || null,
      siteName:       isGuid(s.v['SiteName']) ? s.v['SiteName'] : null,
      address:        s.v.Address || null,
      keyMap:         s.v.KeyMap || null,
      samplingAddress: s.v.SamplingAddress || null,
      samplePointID:  s.v.SamplePointID || null,
      samplePointType: s.v.SamplePointType || null,
      pwsid:          isGuid(s.v['PWSID']) ? s.v['PWSID'] : null,
      // WaterType is now the FK to dbo.WaterTypes (saved to sl.WaterType), not the
      // free-text WaterSource column (G5 parity 2026-09-30). WaterSource has no grid
      // field, so it is left to any explicit v['WaterSource'] (else NULL).
      waterSource:    s.v['WaterSource'] || null,
      waterType:      isGuid(s.v['WaterType']) ? s.v['WaterType'] : null,
      rejectionCriteria: isGuid(s.v['RejectionCriteria']) ? s.v['RejectionCriteria'] : null,
      repeatLocation: isGuid(s.v['RepeatLocation']) ? s.v['RepeatLocation'] : null,
      // Sample-Login lookup-master FK dropdowns (G5 parity 2026-09-30) — send the
      // selected OID (only when it's a GUID; blank/legacy text is dropped).
      bare:             isGuid(s.v['Bare']) ? s.v['Bare'] : null,
      kind:             isGuid(s.v['Kind']) ? s.v['Kind'] : null,
      code:             isGuid(s.v['Code']) ? s.v['Code'] : null,
      components:       isGuid(s.v['Components']) ? s.v['Components'] : null,
      size:             isGuid(s.v['Size']) ? s.v['Size'] : null,
      sanitarian:       isGuid(s.v['Sanitarian']) ? s.v['Sanitarian'] : null,
      sanitarianTemp:   isGuid(s.v['SanitarianTemp']) ? s.v['SanitarianTemp'] : null,
      totalSurfaceArea: isGuid(s.v['TotalSurfaceArea']) ? s.v['TotalSurfaceArea'] : null,
      airVolume:        isGuid(s.v['AirVolume']) ? s.v['AirVolume'] : null,
      siteSource:       isGuid(s.v['SiteSource']) ? s.v['SiteSource'] : null,
      source:           isGuid(s.v['Source']) ? s.v['Source'] : null,
      pwsSystemName:    isGuid(s.v['PWSSystemName']) ? s.v['PWSSystemName'] : null,
      userDefinedDropdown1: isGuid(s.v['UserDefinedDropdown1']) ? s.v['UserDefinedDropdown1'] : null,
      userDefinedDropdown2: isGuid(s.v['UserDefinedDropdown2']) ? s.v['UserDefinedDropdown2'] : null,
      userDefinedDropdown3: isGuid(s.v['UserDefinedDropdown3']) ? s.v['UserDefinedDropdown3'] : null,
      userDefinedDropdown4: isGuid(s.v['UserDefinedDropdown4']) ? s.v['UserDefinedDropdown4'] : null,
      department:     isGuid(s.v['Department']) ? s.v['Department'] : null,
      sampleStatus:   isGuid(s.v['SampleStatus']) ? s.v['SampleStatus'] : null,
      parentSampleID: s.v.ParentSampleID || null,
      parentSampleDate: s.v.ParentSampleDate || null,
      sysSampleCode:  s.v.SysSampleCode || null,
      projectNumber:  s.v.ProjectNumber || null,
      plannedLatitude: s.v.PlannedLatitude || null,
      plannedLongitude: s.v.PlannedLongitude || null,
      chlorineFree:   s.v['FreeColorine(mg/L)'] || null,
      chlorineTotal:  s.v['TotalChlorine(mg/L)'] || null,
      flowRate:       s.v.FlowRate || null,
      volume:         s.v.Volume || null,
      time:           s.v.Time || null,
      startFlow:      s.v.StartFlow || null,
      endFlow:        s.v.EndFlow || null,
      timeStart:      s.v.TimeStart || null,
      timeEnd:        s.v.TimeEnd || null,
      collectorPhone: s.v.CollectorPhone || null,
      samplingEvent:  s.v.SamplingEvent || null,
      alternativeStation: s.v.AlternativeStation || null,
      excludeInvoice: s.v['ExcludeInvoice'] === '1' || s.v['ExcludeInvoice'] === 'true',
      notReport:      s.v['NotReport'] === '1' || s.v['NotReport'] === 'true',
      complianceProject: s.v['ComplianceProject'] === '1' || s.v['ComplianceProject'] === 'true',
      dueDate:        s.v.DueDate || null,
      restTime:       s.v.RestTime || null,
      ph:             s.v.PH || null,
      dissolvedOxygen: s.v.DO || null,
      standardVol:    s.v.StandardVol || null,
      absorptionvol:  s.v.Absorptionvol || null,
      trafficFlowBig: s.v.TrafficFlowBigVechile || null,
      trafficFlowMiddle: s.v.TrafficFlowMiddleVechile || null,
      trafficFlowSmall: s.v.TrafficFlowSmallVechile || null,
      receivedDate:   s.v.ReceivedDate || null,
    }));
    // When samples state is empty (user hasn't navigated to the Login step yet),
    // synthesise sample stubs from noOfSamples + templateTests so Custom Request
    // edits are still included in the payload.  The sampleKey format
    // (JobID + '-' + zero-padded ordinal) matches the SP's ELSE branch that
    // populates @SampleMap from existing SampleLogIn rows when @SamplesJson is null.
    const samplesForTests: Pick<SrSample, 'id' | 'tests'>[] =
      samples.length > 0
        ? samples
        : currentJobId && templateTests.length > 0
          ? Array.from(
              { length: Math.max(1, Number.parseInt(header.noOfSamples, 10) || 1) },
              (_, i) => ({ id: sampleId(currentJobId, i + 1), tests: templateTests }),
            )
          : [];
    // Build one SampleParameter row per SELECTED parameter per sample.
    // When the user has customised parameters (t.params is non-null),
    // only selected parameters are sent. Unselected ones are omitted,
    // which the SP treats as a delete (replace-all semantics).
    // Each entry carries testparameterOid (dbo.Testparameter.Oid) so
    // the SP can insert directly without resolution; testOid
    // (TestMethod.Oid) is kept as a fallback for the auto-seed path.
    const sampleTestsBody = samplesForTests.flatMap(s =>
      s.tests.flatMap(t => {
        const composite = t.method ? `${t.name}|${t.method}` : t.name;
        // For DB-loaded tests prefer the stored UUID; newly-added tests fall
        // back to the display-name lookup so they still resolve correctly.
        const testOid = t.testMethodOid ?? oid('test', composite);
        if (!testOid) return [];
        const cr = customRequest[t.name];
        const statusIdx = SAMPLE_STATUS_OPTS.indexOf(t.status ?? '');
        const base = {
          sampleKey: s.id,
          testOid,
          comp:    t.comp || null,
          // Group the test was added through → SampleParameter.GroupTest (TestGroupName).
          groupName: t.testGroupName || null,
          // Prefer customRequest override; fall back to DB-loaded values on t
          hold:    cr?.hold            ?? t.hold    ?? false,
          tatOid:  cr?.tat ? (oid('tat', cr.tat) ?? null) : (t.tatOid ?? null),
          dueDate: cr?.due             ?? null,
          // Status "Sub Out" IS the sub-out flag (legacy: one SampleParameter.SubOut), so it
          // wins over a stale Custom Request entry that still says false — otherwise the
          // test saved Status=Sub Out with SubOut unchecked (user report 2026-10-08).
          subOut:  t.status === 'Sub Out' ? true : (cr?.subOut ?? t.subOut ?? false),
          status:  statusIdx >= 0 ? statusIdx : 0,
        };
        // If parameters were customised, emit one row per selected param
        if (t.params) {
          const entries = Object.entries(t.params)
            .filter(([, on]) => on)
            .map(([pm]) => ({
              ...base,
              testparameterOid: cachedParamOid(t, pm) ?? null,
            }))
            .filter(e => e.testparameterOid);
          return entries.length > 0 ? entries : [{ ...base, testparameterOid: null }];
        }
        // All parameters selected (default) — let the SP resolve via CROSS APPLY
        return [{ ...base, testparameterOid: null }];
      }),
    );
    // Received Dates (dbo.SC_ReceivedDates) — replace-all save/edit/delete. The
    // sub-tab stores display values (by/relinq/tat/matrix/cat); the API resolves
    // them to Oids. `oid` round-trips so an existing row UPDATEs, not duplicates.
    // Guarded by rdLoaded: for an existing job we only make the sub-tab
    // authoritative (and thus able to delete rows) AFTER its rows have loaded —
    // sending null before that leaves the table untouched.
    // During a COC "Import Pre-Log" auto-save the chosen matrix lives in
    // matrixOverrideRef, not yet in header.matrices — use whichever is set so the
    // Received Dates row is STILL synthesised on import (G5 creates it too:
    // SampleRegistrationViewController.cs:6714 fires in the import's commit).
    const effectiveMatrices = header.matrices.length
      ? header.matrices
      : (matrixOverrideRef.current ?? []);
    const receivedDatesBody = rdLoaded
      ? (tabs.receivedDates.length > 0
          ? tabs.receivedDates.map((r: SrReceivedDate) => ({
              oid: r.oid || null,
              receivedDate: wireDateTime(r.date),
              receivedBy: r.by || null,
              relinquishedBy: r.relinq || null,
              tat: r.tat || null,
              sampleMatrix: r.matrix.join('; ') || null,
              sampleCategory: r.cat.join('; ') || null,
              coolerId: r.cooler || null,
              noOfSamples: r.n || 1,
            }))
          // G5 parity (SampleRegistrationViewController.cs:6714 —
          // `IsNewObject(obj) && !obj.IsSampling`): SAVE synthesises ONE Received
          // Date row from the header ONLY for REGULAR (non-sampling) jobs, where
          // the sample is physically received at registration. A PRE-SCHEDULED
          // job has no received date yet (it is received later during field
          // data / transfer), so legacy leaves its Received Dates tab EMPTY —
          // don't synthesise a row for it (user request 2026-09-26).
          : (effectiveMatrices.length > 0 && header.registrationType !== 'Pre-scheduled'
              ? [{
                  oid: null,
                  receivedDate: wireDateTime(header.receivedDate),
                  receivedBy: header.receivedBy || null,
                  relinquishedBy: null,
                  tat: header.tat || null,
                  sampleMatrix: effectiveMatrices.join('; ') || null,
                  sampleCategory: header.sampleCategory.join('; ') || null,
                  coolerId: null,
                  noOfSamples: Math.max(1, parseInt(header.noOfSamples, 10) || 1),
                }]
              : []))
      : null;

    // Bottle allocations: one row per (sample × test × bottle letter).
    // Uses bd[letter] when per-bottle detail exists; falls back to the test-level
    // container/preserv/storage values.
    // testMethodOid: DB-loaded tests carry the Oid; newly-added tests use the test
    // NAME as a fallback — the writer's ResolveFkAsync resolves it via Fk.Test.
    const bottlesBody = samples.flatMap(s =>
      s.tests.flatMap(t => {
        if (t.isFieldTest) return [];   // legacy: no SampleBottleAllocation for field tests
        const letters = (t.bottle || '').split(',').map(l => l.trim()).filter(Boolean);
        if (!letters.length) return [];
        const testMethodKey = t.testMethodOid ?? t.name;  // Oid or display name for resolution
        if (!testMethodKey) return [];
        return letters.map(letter => {
          const detail = t.bd?.[letter];
          return {
            sampleKey:        s.id,
            testMethodOid:    testMethodKey,
            bottleId:         letter,
            container:        detail?.container        ?? t.container ?? null,
            preservative:     detail?.preserv          ?? t.preserv   ?? null,
            storage:          detail?.storage          ?? t.storage   ?? null,
            storageCondition: detail?.storageCondition ?? null,
          };
        });
      }),
    );

    // ── #8/#9: field-method results captured in Sample Registration ──
    // ALPACA enters Result / Units / AnalysisDate / AnalyzedBy for FIELD tests
    // right here (SampleParameter_ListView_Copy_SampleRegistration batch edit);
    // lab-method results are entered later in Result Entry. Persist one row per
    // field-test parameter so the values survive a reload instead of resetting to
    // the auto defaults (user report 2026-10-09). AnalysisDate defaults to the
    // collection date/time and AnalyzedBy to the collector; a user override wins.
    const collectorNameOf = (s: SrSample): string => {
      const cid = s.v.Collector ?? '';
      if (!cid) return '';
      return collectorMap[cid] ?? collectorMap[cid.toLowerCase()] ?? cid;
    };
    const autoAnalysisDate = (s: SrSample): string => {
      const d = s.v.DateCollected ?? '';
      const tm = s.v.TimeCollected ?? '';
      return d && tm ? `${d}T${tm}` : d ? `${d}T00:00` : '';
    };
    const fieldResultsBody = samples.flatMap(s =>
      s.tests.filter(t => t.isFieldTest).flatMap(t => {
        const composite = t.method ? `${t.name}|${t.method}` : t.name;
        const testOid = t.testMethodOid ?? oid('test', composite);
        if (!testOid) return [];
        const params = (cachedParams(t) ?? [t.name]).filter(pm => !t.params || t.params[pm] !== false);
        return params.flatMap(pm => {
          const pd = t.paramData?.[pm];
          const result       = pd?.result || null;
          const units        = pd?.units || null;
          // stored override wins over the collection-date / collector default
          const analyzedDate = (pd?.analysisDate || autoAnalysisDate(s)) || null;
          const analyzedBy   = (pd?.analyzedBy || collectorNameOf(s)) || null;
          // Skip a wholly-empty row so the UPSERT never nulls out a param that
          // was already analysed (data-loss guard).
          if (!result && !units && !analyzedDate && !analyzedBy) return [];
          return [{ sampleKey: s.id, testOid, parameterName: pm, result, units, analyzedDate, analyzedBy }];
        });
      }),
    );

    const body = {
      isSampling: header.registrationType === 'Pre-scheduled',
      // FK fields — every one goes through display→Oid resolution. Client
      // prefers the exact captured Oid (unambiguous for duplicate names).
      clientName:      header.clientOid || oid('client', header.client),
      clientContact:   oid('contact',   header.clientContact),
      // Account Number → Samplecheckin.SelectedAccount (ClientAccountNumber FK).
      selectedAccount: oid('account',   header.accountNumber),
      invoiceContact:  oid('invoicecontact', header.invoiceContact),
      quoteId:         oid('quote',     header.quoteId),
      po: header.poNumber || null,
      paymentStatus:   oid('payment',   header.paymentStatus),
      manifestNumbers: header.manifestNumber ? Number(header.manifestNumber) || null : null,
      projectId:       header.projectId ? (header.projectOid || oid('project', header.projectId)) : null,
      projectCategory: oid('category',  header.projectCategory),
      projectManager:  oid('pm',        header.projectManager),
      permitLibrary:   oid('permit',    header.permit),
      cocId:           oid('cocid',     header.cocId),
      cocSource:       oid('cocid',     header.cocSource),
      jobInactivation: oid('inact',     header.jobInactivation),
      cancelJob: header.cancelJob || false,
      receivedDate: wireDateTime(header.receivedDate),
      receivedBy:      oid('employee',  header.receivedBy),
      dateCollected: wireDateTime(header.dateCollected),
      timeCollected: header.timeCollected || null,
      // Multi-select FK fields — resolve every entry, join with '; '
      relinquishedBy:  oidList('relinq',         header.relinquishedBy),
      collector:       oidList('collector',      header.collector),
      dateExpect: header.dateExpect || null,
      // COC Import: when the user has NOT chosen a matrix, fall back to the
      // chosen pre-log's matrix (matrixOverrideRef) so the auto-save passes the
      // server's "Sample Matrix is required" check — the import then overwrites
      // it from the pre-log anyway (user report 2026-09-24).
      sampleMatrices:  oidList('matrix', header.matrices.length ? header.matrices : (matrixOverrideRef.current ?? [])),
      sampleCategory:  oidList('sampleCategory', header.sampleCategory),
      test:            oidList('test',           header.testTemplate),
      numberOfSample: Number(header.noOfSamples) || 1,
      coolerId: header.coolerId || null,
      remark: header.remark || null,
      tat:             oid('tat',       header.tat),
      dueDate: header.dueDate || null,
      // ReportTemplate is a plain string column — no FK, no Oid resolution.
      reportTemplate: header.reportTemplate || null,
      eddTemplate:     oid('eddtpl',    header.eddTemplate),
      // EDDFormat is the legacy Samplecheckin.EDDFormat enum (INT). The picker
      // stores the DISPLAY label ("Export to CSV"); map it to the enum value.
      eddFormat: eddFormatToInt(header.eddFormat),
      reportDeliveryAddress: header.reportDeliveryAddress || null,
      reportDeliveryMethod: header.reportDeliveryMethod || null,
      // Phase 1 child collections
      samples:     samplesBody.length ? samplesBody : null,
      headerTests: headerTestOids.length ? headerTestOids : null,
      // Samples are on the form but every test was deleted → send an EXPLICIT empty
      // set so the server clears them; null would mean "not sent" and leave the
      // deleted tests in place (bug 3, 2026-10-16).
      sampleTests: sampleTestsBody.length ? sampleTestsBody
        : (currentJobId && samples.length > 0 ? [] : null),
      bottles:     bottlesBody.length ? bottlesBody : null,
      // Field-method results (Result/Units/AnalysisDate/AnalyzedBy) — #8/#9.
      fieldResults: fieldResultsBody.length ? fieldResultsBody : null,
      // Received Dates sub-tab (save/edit/delete via replace-all)
      receivedDates: receivedDatesBody,

      // ── Additional child tab collections (guarded by loaded flags) ──
      notes: notesLoaded
        ? tabs.notes.map((n: SrNote) => ({
            oid: n.oid || null,
            title: n.title || null,
            html: n.html || null,
            plainText: n.plainText || null,
            sort: n.sort,
            sourceId: n.sourceId || null,
            noteSource: n.noteSource || null,
          }))
        : null,

      // A job carries SEVERAL SCCs (legacy 1:N) — send every record; [] removes them all.
      scc: sccLoaded
        ? tabs.sccRecords.map(s => {
            return {
              oid: s.oid || null,
              checkInBy: s.by || null,
              checkInDate: wireDateTime(s.date),
              visualMatrix: s.matrix || null,
              temperature: s.temp || null,
              correctedTemperature: s.corr || null,
              thermometerId: s.thermo || null,
              samplePH: s.ph || null,
              phPaperId: s.paper || null,
              receivedDate: s.recv || null,
              checkPoints: SCC_POINTS.map((p, i) => ({
                checkPointName: p[0],
                isYes: s.points[i] === 'yes',
                isNo: s.points[i] === 'no',
                isNA: s.points[i] === 'na',
                description: s.descriptions[i] ?? '',
              })),
              comments: s.comments.map(c => ({ comment: c.text })),
            };
          })
        : undefined,

      itemCharges: icLoaded
        ? tabs.itemCharges.map((ic: SrItemCharge) => ({
            oid: ic.oid || null,
            itemPrice: ic.itemPriceOid || null,   // send FK Oid, not display name
            description: ic.desc || null,
            unitPrice: ic.price,
            qty: ic.qty,
            amount: ic.amount,
            finalAmount: ic.final,
            npUnitPrice: ic.npPrice,
            discount: ic.disc,
            remark: ic.remark || null,
          }))
        : null,

      attachments: attLoaded
        ? tabs.attachments.map((a: SrAttachment) => ({
            oid: a.oid || null,
            name: a.name || null,
            category: Number.isFinite(Number(a.cat)) ? Number(a.cat) : 5,
            operator: a.opOid || a.op || null,
            fileDataOid: a.fileDataOid || null,
            date: wireDateTime(a.date),
            comment: a.comment || null,
            receivedDate: a.recv || null,
          }))
        : null,

      images: imgLoaded
        ? tabs.images.map((img: SrImage) => ({
            oid: img.oid || null,
            name: img.name || null,
            fileDataOid: img.fileDataOid || null,
            comment: img.comment || null,
            sort: img.sort,
          }))
        : null,

      // Duplicate-parameter justifications captured in the Test Assignment popup —
      // each writes one audit row (FormName "Sample Registration", Comment=reason).
      duplicateJustifications: dupJustifications.length > 0
        ? dupJustifications.map(d => ({
            sampleKey: d.sampleKey,
            parameterName: d.paramName,
            testNames: d.testNames.join(', '),
            reason: d.reason,
          }))
        : null,
    };

    let res: { data: { success: boolean; data: { oid: string; jobId: string; action: string } } };
    if (currentJobId && jobOid) {
      res = await api.put(`/api/v1/sample-registration/${jobOid}`, body);
    } else {
      res = await api.post('/api/v1/sample-registration', body);
    }
    if (!res.data.success) {
      // Surface the real server-side error so the user (and AI_ExceptionLog
      // triage) can act on it. Same shape used by every AI controller:
      // { success:false, error: { code, message } }.
      const err = (res.data as unknown as { error?: { message?: string } }).error;
      throw new Error(err?.message ?? 'Save failed');
    }
    const savedOid = res.data.data.oid;
    if (savedOid) { setJobOid(savedOid); lastSavedOidRef.current = savedOid; }

    // Re-fetch all child tabs so their freshly-minted server Oids populate
    // the sub-tabs — without this a SECOND save would re-INSERT (duplicate)
    // the same rows instead of UPDATE-ing them.
    if (savedOid) {
      const oidEnc = encodeURIComponent(savedOid);
      const refetch = async <T,>(
        path: string, map: (r: T) => unknown, tabKey: keyof SrTabState,
        setLoaded: (v: boolean) => void, sent: unknown, isSingle?: boolean,
      ) => {
        if (sent === null || sent === undefined) return;
        try {
          const r = await api.get(`/api/v1/sample-registration/${oidEnc}/${path}`);
          const raw = (r.data as ApiSuccess<T | T[]>).data;
          if (isSingle) {
            const mapped = raw ? [map(raw as T)] : [];
            setTabs(t => ({ ...t, [tabKey]: mapped }));
          } else {
            const arr = (raw as T[] | null) ?? [];
            setTabs(t => ({ ...t, [tabKey]: arr.map(map) }));
          }
          setLoaded(true);
        } catch { /* non-fatal; server logged it */ }
      };
      await Promise.all([
        refetch<ReceivedDateRowDto>('received-dates', mapReceivedDateRow, 'receivedDates', setRdLoaded, receivedDatesBody),
        refetch<NoteRowDto>('notes', mapNoteRow, 'notes', setNotesLoaded, body.notes),
        refetch<SccDto>('scc', mapSccRow as (r: SccDto) => unknown, 'sccRecords', setSccLoaded, body.scc),
        refetch<ItemChargeRowDto>('item-charges', mapItemChargeRow, 'itemCharges', setIcLoaded, body.itemCharges),
        refetch<AttachmentRowDto>('attachments', mapAttachmentRow, 'attachments', setAttLoaded, body.attachments),
        refetch<ImageRowDto>('images', mapImageRow, 'images', setImgLoaded, body.images),
      ]);

      // Re-hydrate the sample rows from the DB so the flow-bar counts
      // (Samples / Tests / Containers) reflect what was actually persisted —
      // the save generates `numberOfSample` samples server-side even when the
      // user never opened the Samples step, so the in-memory `samples` array is
      // still empty here. G5 parity: its pill captions recompute from the
      // SampleLogIn / SampleParameter counts after every save
      // (SampleRegistrationViewController.cs ~9619-9651).
      try {
        const slRes = await api.get(`/api/v1/sample-registration/${oidEnc}/sample-logins`);
        const slMapped = mapSampleLoginsPayload((slRes.data as ApiSuccess<SampleLoginsDto | null>).data);
        serverSamplesRef.current = slMapped;   // saved baseline for duplicate grandfathering
        if (slMapped.length > 0) {
          setSamples(slMapped);
          // Keep "No Of Samples" in step with the persisted count (G5 parity).
          setHeader(h => ({ ...h, noOfSamples: String(slMapped.length) }));
        }
      } catch { /* non-fatal; server logged it */ }
    }
    return res.data.data.jobId;
  }, [currentJobId, jobOid, header, samples, customRequest, formLookups, rdLoaded, notesLoaded,
      sccLoaded, icLoaded, attLoaded, imgLoaded, tabs, dupJustifications]);

  const prepareForSubViews = useCallback(async (): Promise<boolean> => {
    if (currentJobId) { ensureSamples(currentJobId); return true; }
    if (missingRequired.length > 0) {
      await warnMissing('Fill these before adding samples:');
      return false;
    }
    if (!Number.parseInt(header.noOfSamples, 10)) patch({ noOfSamples: '1' });
    try {
      const job = await persist();
      // Bug: entering the Samples/Tests/Containers tabs auto-persists a brand
      // new job, but `currentJobId`/`header.jobId` were never updated with the
      // minted ID — the check-in header kept showing "Autogenerate" after
      // navigating back to it, and a SECOND save would POST a duplicate job
      // instead of PUT-ing the one just created (currentJobId was still null).
      setCurrentJobId(job);
      // The server created the job at Pending Submission/Sampling (not the draft
      // Pending Saving) — show it now, not only after the next explicit Save.
      patch({ jobId: job, status: pendingStatusFor(header.registrationType) });
      ensureSamples(job);
      return true;
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to create the job.'));
      return false;
    }
  }, [currentJobId, missingRequired.length, header.noOfSamples, header.registrationType, ensureSamples, warnMissing, patch, notify, persist]);

  /** Save a NEW registration just enough to mint its JobID (legacy commits the parent when
   *  New is clicked in a nested list, e.g. the Sample Condition Check). No samples are
   *  generated. Returns the JobID, or null when required fields are missing / save fails. */
  const ensureJobId = useCallback(async (): Promise<string | null> => {
    if (currentJobId) return currentJobId;
    if (missingRequired.length > 0) {
      await warnMissing('Fill these before adding a Sample Condition Check:');
      return null;
    }
    try {
      const job = await persist();
      setCurrentJobId(job);
      patch({ jobId: job, status: pendingStatusFor(header.registrationType) });
      return job;
    } catch (e) {
      notify.error(apiErrMsg(e, 'Failed to create the job.'));
      return null;
    }
  }, [currentJobId, missingRequired.length, header.registrationType, warnMissing, persist, patch, notify]);

  const goToStep = useCallback(async (next: StepIndex) => {
    if (next >= 1 && !(await prepareForSubViews())) return;
    setStep(next);
    if (next === 1) setSlTab('login');
    if (next === 2) setSlTab('testview');
    if (next === 3) setSlTab('containers');
    window.scrollTo(0, 0);
  }, [prepareForSubViews]);

  const doSave = useCallback(async (after: 'stay' | 'close' | 'new') => {
    setTriedSave(true);
    if (missingRequired.length > 0) {
      await warnMissing('Please fill in the following fields before saving:');
      return;
    }
    // ALPACA parity — Samplecheckin rule DueDate_GreaterOrEqual_RecievedDate
    // (Samplecheckin.cs:60, skips empty values): a Due Date before the Received Date
    // blocks the save.
    const dueDay = toDateInput(header.dueDate);
    const recvDay = toDateInput(header.receivedDate);
    if (dueDay && recvDay && dueDay < recvDay) {
      notify.warning('Due Date must be greater than or equal to the Received Date.');
      return;
    }
    // ── Site Name required on every sample of a Pre-scheduled (Sampling) job ──
    // G5 parity — SampleLogInViewController.Save_SampleLogin blocks the Sample
    // Login save with "Enter the site name." when any SampleLogIn has
    // StationLocation == null AND JobID.IsSampling. Only checked once the Sample
    // Login grid has rows (samples generated); a Registration-step save with no
    // rows is unaffected, exactly as legacy (the check lives on that sub-view).
    const isPrescheduled = /^pre-?scheduled$/i.test((header.registrationType ?? '').trim());
    if (isPrescheduled && samples.length > 0
        && samples.some(s => !(s.v['SiteName'] ?? '').trim())) {
      notify.warning('Enter the site name.');
      return;
    }
    // ── Duplicate analysis validation (legacy parity: ValidateDuplicateAnalysis) ──
    // ALPACA only prompts for NEWLY-introduced duplicates: a duplicate already
    // present in the SAVED data is grandfathered (it was accepted at its first
    // save), and a parameter already justified for the job is filtered too. So we
    // exclude both the saved-baseline duplicates and the justified params.
    const baselineDupKeys = new Set(
      findDuplicateAnalyses(serverSamplesRef.current, cachedParams)
        .map(d => `${d.sampleId}|${d.paramName}`));
    const dupes = findDuplicateAnalyses(samples, cachedParams)
      .filter(d => !baselineDupKeys.has(`${d.sampleId}|${d.paramName}`) && !justifiedParams.has(d.paramName));
    if (dupes.length > 0) {
      const lines = dupes.map(d => `• ${d.paramName} in [${d.testNames.join(', ')}] (${d.sampleId})`);
      const ok = await confirm({
        title: 'Duplicate Parameters Found',
        message: `The following parameters appear in more than one test:\n\n${lines.join('\n')}\n\nDo you want to proceed?`,
        tone: 'danger',
      });
      if (!ok) return;
    }

    const wasNew = isNew;
    let mintedJob = currentJobId ?? '';
    await runSave({
      formName: SAMPLE_REGISTRATION_FORM,
      auditAction: wasNew ? 'create' : 'edit',
      save: async () => { mintedJob = await persist(); },
      // A milestone success (§13) when the save mints a NEW Job ID.
      successDialog: wasNew
        ? () => ({
          type: 'success' as const,
          title: 'Job Created Successfully',
          idLabel: 'Job ID',
          idValue: mintedJob,
          rows: [
            { label: 'Status', value: pendingStatusFor(header.registrationType) },
            { label: 'Client', value: header.client },
            // The save persists `numberOfSample` samples (from No Of Samples)
            // even when the user never opened the Samples step, so `counts.samples`
            // (= in-memory rows) is 0 there. Report what was actually persisted.
            { label: 'Samples', value: String(counts.samples > 0
                ? counts.samples
                : (Number.parseInt(header.noOfSamples, 10) || 1)) },
          ],
          note: 'Job saved. Samples, tests, and per-sample test assignments persisted (Phase 1).',
          actions: [
            { label: '← Keep Editing', value: 'stay' },
            { label: 'Sample Login →', value: 'login' },
            { label: after === 'new' ? 'Register Next →' : after === 'close' ? 'Go to List →' : 'OK', primary: true, value: 'next' },
          ],
        })
        : undefined,
      // Edit save: plain "Saved successfully." — no Job ID in the message (user
      // request 2026-09-24, reverting the earlier "Job {id} saved successfully.").
      // New jobs still get the milestone dialog above with the minted Job ID.
      onSaved: (action) => {
        explicitlySavedRef.current = true;   // an explicit Save — never discard on Cancel now
        setCurrentJobId(mintedJob);
        // SILENT (setHeader, not patch): reflects the JUST-PERSISTED jobId/status,
        // so it must not re-dirty the form — otherwise Cancel / navigating away
        // right after a save wrongly prompts "Unsaved changes". Clear the flag AFTER
        // the update so it wins (the old order set it false then patch re-set true).
        setHeader(h => ({ ...h, jobId: mintedJob, status: submitted ? h.status : pendingStatusFor(h.registrationType) }));
        dirtyRef.current = false;
        // The justifications were audited by this save — clear so a later save
        // doesn't re-send (and re-audit) them.
        setDupJustifications([]);
        onSaved();
        // "Sample Login →" on the "Job Created Successfully" dialog (line ~1612)
        // used to be indistinguishable from every other action — the dialog's
        // resolved value was discarded, so clicking it left the user stranded on
        // the Registration step. Jump straight to the Sample Login grid instead.
        if (action === 'login') {
          ensureSamples(mintedJob);
          setStep(1);
          setSlTab('login');
          window.scrollTo(0, 0);
          return;
        }
        if (after === 'close') { onClose(); return; }
        if (after === 'new') {
          setHeader(seedHeader(null, registrationType, nowLocal, user, undefined));
          setSamples([]);
          jobHadSamplesRef.current = false;
          setTabs(seedTabs(nowLocal, user));
          setCustomRequest({});
          setCurrentJobId(null);
          setJobOid(null);
          setRdLoaded(true);   // fresh job — nothing to load
          setNotesLoaded(true);
          setSccLoaded(true);
          setIcLoaded(true);
          setAttLoaded(true);
          setImgLoaded(true);
          setTriedSave(false);
          setStep(0);
        }
      },
      onError: (e) => notify.error(apiErrMsg(e, 'Save failed. Please try again.')),
    });
  }, [
    missingRequired.length, warnMissing, isNew, currentJobId, runSave, persist, header,
    counts.samples, samples, submitted, patch, onSaved, onClose, registrationType, nowLocal, user, notify,
    ensureSamples, dupJustifications, justifiedParams,
  ]);

  /** Reset Job — soft-deletes all children (samples, tests, parameters, bottles,
   *  custom due-dates, extra received-dates) while keeping the Samplecheckin header
   *  and its JobID. Only allowed on Pending (pre-submit) jobs.
   *  Legacy: BtnResetJobid_Execute → Resetjobid SP. */
  const resetJobId = useCallback(async () => {
    if (!currentJobId || !jobOid) { notify.warning('Save the registration first.'); return; }
    // ALPACA parity (BtnResetJobid_Execute): only a pre-submit job (Samplecheckin.Status
    // == 0, StatusName "Pending Submission") can be reset. A submitted / progressed job
    // (Status 4+, e.g. "Submitted" or "Pending Sampling") shows the blocking info and is
    // NOT prompted — the server SP enforces the same Status==0 rule as a final guard.
    // Gate on the numeric sign-off Status like legacy (sc.Status == 0), NOT the status
    // TEXT: a pre-scheduled job at "Pending Sampling" is still unsubmitted (Status 0)
    // and must be resettable (user report 2026-10-06).
    if (header.signoffStatus !== 0) {
      notify.info('The submitted job ID cannot be reset. ');
      return;
    }
    const ok = await confirm({
      title: 'Reset Job ID',
      message: 'The sample tests and parameters are cleared..! Do you want to continue?',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/sample-registration/${jobOid}/reset`);
      const data = res.data?.data ?? {};
      const cleared = data.SamplesCleared ?? data.samplesCleared ?? 0;
      // The SP soft-deletes every child (samples, tests, bottles, custom due
      // dates, extra received dates) and clears the header sample fields, but
      // the in-memory state still held them — so the flow-bar chips stayed at
      // Samples/Tests/Containers from BEFORE the reset (user report: G5 shows
      // 0/0/0 after a reset, we did not, because resetJobId only called
      // onSaved() which refreshes the LIST, never the open detail). Clear the
      // sample rows immediately (drives `counts` → 0/0/0) and bump reloadTick
      // to re-fetch the whole detail so the header + sub-tabs reflect the
      // cleared DB state — the same reload the COC / Quote imports do. G5
      // parity: BtnResetJobid_Execute reloads the view after Resetjobid.
      setSamples([]);
      jobHadSamplesRef.current = false;
      serverSamplesRef.current = [];
      dirtyRef.current = false;
      setReloadTick(t => t + 1);
      notify.success(`Job ${currentJobId} has been successfully reset. ${cleared} sample(s) cleared.`);
      onSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Reset failed. Please try again.';
      notify.warning(msg);
    }
  }, [currentJobId, jobOid, header.signoffStatus, confirm, onSaved, notify]);

  const doSubmit = useCallback(async () => {
    if (!currentJobId || !jobOid) {
      notify.warning('Save the registration first, then click Submit.');
      return;
    }
    const ok = await confirm({
      title: 'Submit registration',
      message: `Submit job ${currentJobId}? It is released to Sample Receipt Notification.`,
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/sample-registration/${jobOid}/submit`);
      const data = res.data?.data ?? {};
      // Submit PERSISTS the job, so the status update is silent (setHeader) and the
      // dirty flag is cleared — otherwise navigating away after Submit wrongly
      // prompts "Unsaved changes will be lost".
      explicitlySavedRef.current = true;   // a definitive commit — never discard on Cancel
      setHeader(h => ({ ...h, status: data.newStatus ?? 'Submitted', signoffStatus: 4 }));
      dirtyRef.current = false;
      onSaved();
      notify.success('Submit completed successfully.');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Submit failed. Please try again.';
      notify.warning(msg);
    }
  }, [currentJobId, jobOid, confirm, patch, onSaved, notify]);

  const doSaveAs = useCallback(async () => {
    if (!currentJobId || !jobOid) {
      notify.warning('Save the registration first.');
      return;
    }
    const ok = await confirm({
      title: 'Save As',
      message: 'Do you want to save as a new Job ID?',
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/sample-registration/${jobOid}/save-as`);
      const data = res.data?.data ?? {};
      const newJobId: string = data.JobId ?? data.jobId ?? '';
      const newOid: string = data.Oid ?? data.oid ?? '';
      explicitlySavedRef.current = true;   // committed the source job — don't discard it
      notify.success(`${newJobId} JobID created successfully.`);
      onSaved();
      if (newOid) onNavigate(newOid);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Save As failed. Please try again.';
      notify.warning(msg);
    }
  }, [currentJobId, jobOid, confirm, onSaved, onNavigate, notify]);

  const doRecalculateInvoice = useCallback(async () => {
    if (!currentJobId) { notify.warning('Save the registration first.'); return; }
    const ok = await confirm({
      title: 'Recalculate Invoice',
      message: `Do you want to recalculate the invoice summary for job ${currentJobId}?`,
    });
    if (!ok) return;
    try {
      await api.post(`/api/v1/sample-registration/${encodeURIComponent(currentJobId)}/recalculate-invoice`);
      notify.success('Invoice recalculated successfully.');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Recalculate invoice failed.';
      notify.warning(msg);
    }
  }, [currentJobId, confirm, notify]);

  const doDeleteJob = useCallback(async () => {
    if (!currentJobId) return;
    // G5 parity: a job whose samples are logged in cannot be deleted — ALPACA blocks
    // it with this exact message. Show it immediately (no misleading "cannot be
    // undone" confirm) when we already know samples exist; the server enforces the
    // same rule as the authority for anything the client hasn't loaded.
    if (samples.length > 0) {
      notify.warning('The jobID cannot be deleted! it is used in sample login.');
      return;
    }
    const ok = await confirm({
      title: 'Delete Job',
      message: `Delete job ${currentJobId}? This will soft-delete the entire registration. This cannot be undone.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post('/api/v1/sampling/registration/delete', { jobIds: [currentJobId] });
      notify.success(`Job ${currentJobId} deleted.`);
      // Refresh the list BEFORE closing — otherwise the user lands back on a grid
      // still showing the job they just deleted (user report 2026-10-03).
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Delete failed.';
      notify.warning(msg);
    }
  }, [currentJobId, samples.length, confirm, notify, onSaved, onClose]);

  /**
   * "Import Pre-Log" (COC) is a ONE-SHOT create action, only ever offered on a
   * brand-new, UNSAVED record — RegistrationForm's Import button disables
   * itself the moment `savedJob` is true (business decision 2026-09-01: once
   * a job has a real Job ID, Import Pre-Log is closed — it never runs again
   * on an already-created job). AI's REST API has no single atomic
   * "create + import" endpoint the way XAF's one accept-handler does (it
   * mints the JobID inline and commits job + samples + tests + bottles
   * together), so this silently auto-saves the header first (no confirm
   * dialog — mirrors the one seamless legacy click) to mint a real Job
   * ID/Oid, then opens the picker. Since the button is unreachable once
   * `jobOid` is set, the `!jobOid` guard below is effectively always true in
   * practice — kept as a defensive no-op rather than assumed.
   */
  const handleImport = useCallback(async (kind: 'quote' | 'coc') => {
    if (kind === 'quote' && !header.quoteId) {
      notify.warning('Select a Quote ID first.');
      return;
    }
    // COC "Import Pre-Log" — legacy parity (SampleRegistrationViewController
    // .btnCOCImport_Execute): only Client + Project ID (+ Date Expected when
    // pre-scheduled) are required; the Sample Matrix is NOT — it is auto-applied
    // from the chosen pre-log. So DON'T pre-save here (the save needs a matrix);
    // just open the picker, and the accept step saves with the pre-log's matrix.
    if (kind === 'coc') {
      const missingForCoc = missingRequired.filter(m => m.id !== 'sr-f-matrix');
      if (!jobOid && missingForCoc.length > 0) {
        await warnMissing('Fill these before importing a Pre-Log:', missingForCoc);
        return;
      }
      setImportKind('coc');
      return;
    }
    // Quote import keeps the original pre-save (matrix required) flow.
    if (!jobOid) {
      if (missingRequired.length > 0) { await warnMissing('Fill these before importing:'); return; }
      try {
        const mintedJob = await persist();
        dirtyRef.current = false;
        setCurrentJobId(mintedJob);
        patch({ jobId: mintedJob, status: pendingStatusFor(header.registrationType) });
      } catch (e) {
        notify.error(apiErrMsg(e, 'Could not save the registration — try Save first.'));
        return;
      }
    }
    setImportKind(kind);
  }, [jobOid, missingRequired, warnMissing, persist, patch, notify, header.quoteId]);

  /** Ensure a saved job exists for a COC import, applying the chosen pre-log's
   *  matrix to the auto-save (legacy: matrix comes from the pre-log, not the
   *  user). Returns the job Oid for the import POST. */
  const ensureJobForCoc = useCallback(async (matrixNames: string[]): Promise<string> => {
    if (jobOid) return jobOid;
    matrixOverrideRef.current = matrixNames;
    try {
      const mintedJob = await persist();          // sets jobOid state + lastSavedOidRef
      dirtyRef.current = false;
      setCurrentJobId(mintedJob);
      // G5 parity (COC import Sample Matrix, user report): legacy sets
      // `objsamplecheckin.SampleMatries = objCOCSettings.SampleMatries` on import,
      // so the header Sample Matrix shows the pre-log's matrix. The DB gets it
      // right (the auto-save uses matrixOverrideRef), but the post-import reload
      // effect is keyed on the (still-null) `jobId` PROP for a brand-new job and
      // returns early — so the visible header never refreshed and the field read
      // "Select...". Reflect the imported matrix into the header directly here.
      patch({
        jobId: mintedJob,
        status: pendingStatusFor(header.registrationType),
        ...(matrixNames.length ? { matrices: matrixNames } : {}),
      });
    } finally {
      matrixOverrideRef.current = null;
    }
    const oid = lastSavedOidRef.current;
    if (!oid) throw new Error('Could not create the job for import.');
    return oid;
  }, [jobOid, persist, patch]);

  // ── Lookup quick-add ──────────────────────────────────────────────────────
  const addLookupRow = (kind: SrLookupKey, row: string[], oid?: string) => {
    // Always add the new record to its lookup so it appears in the dropdown.
    // Client / Project rows carry their Oid as a hidden trailing element (see
    // mapLookupRows); without it the picked row had no Oid and the old code also
    // dropped oidByLabel, so a just-added record could not be saved or scoped.
    const lookupRow = (kind === 'client' || kind === 'project') && oid ? [...row, oid] : row;
    setLookups(l => ({
      ...l,
      [kind]: {
        ...l[kind],
        rows: [lookupRow, ...l[kind].rows],
        oidByLabel: oid && row[0] && kind !== 'client' && kind !== 'project'
          ? { ...l[kind].oidByLabel, [row[0]]: oid } : l[kind].oidByLabel,
      },
    }));

    if (kind === 'collector') {
      // Collector (Samples-toolbar "Add Collector" OR the header "+"): add it to the
      // grid's Collector dropdown so it is selectable there, but DO NOT select it —
      // the user picks it manually, like ALPACA (user request 2026-10-06; was
      // auto-selected into the active row before). The grid dropdown reads
      // config.collectors, not the header lookups, so this hand-off is still needed.
      setPendingGridCollector({ oid: oid ?? '', name: row[0] ?? '' });
      notify.success(`${row[0]} added.`);
    } else if (newLookupField && !MULTI_LOOKUPS.includes(kind)) {
      // Single-value fields (Client, Project, Contact, …): select the new value into
      // the field that opened the dialog (prototype parity).
      const value = row[0] ?? '';
      const p: Partial<SrHeader> = { [newLookupField]: value } as Partial<SrHeader>;
      if (kind === 'client') { p.clientAddress = row[1] ?? ''; p.clientPhone = row[2] ?? ''; p.clientOid = oid ?? ''; }
      if (kind === 'project') { p.projectName = row[1] ?? ''; p.projectLocation = row[2] ?? ''; p.projectOid = oid ?? ''; }
      patch(p);
      notify.success(`${row[0]} added and selected.`);
    } else {
      // Relinquished By (multi) header "+": added to the dropdown ONLY — the user
      // selects it manually, like ALPACA (user request 2026-10-06). Deliberately does
      // NOT assign the string[] field (assigning a bare string also blanked the page).
      notify.success(`${row[0]} added.`);
    }
    setNewLookupKind(null);
    setNewLookupField(null);
  };

  const openNewLookup = (kind: SrLookupKey) => {
    setNewLookupKind(kind);
    setNewLookupField(FIELD_FOR_LOOKUP[kind] ?? null);
  };

  const confirmAction = (title: string, message: string, run: () => void, danger?: boolean) => {
    void confirm({ title, message, tone: danger ? 'danger' : undefined }).then(ok => { if (ok) run(); });
  };

  // ── Prev / next record ────────────────────────────────────────────────────
  const idx = currentJobId ? siblingIds.indexOf(currentJobId) : -1;
  const prevId = idx > 0 ? siblingIds[idx - 1] : undefined;
  const nextId = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : undefined;

  const recordLabel = currentJobId ?? `New (${header.registrationType})`;
  const charge = useMemo(() => chargeRows(samples), [samples]);

  // ── Action bar ────────────────────────────────────────────────────────────
  // Plain functions invoked inline ({saveTrio()}), not inner components — a
  // component declared in a render body remounts its subtree every render.
  // Action-bar order (user request 2026-09-24): Save · Save As · Submit · Cancel.
  // Save and Close / Save and New were removed; Save As + Submit moved here NEXT to
  // Save (out of the left form-specific group). Save As + Submit are saved-jobs-only.
  const saveTrio = () => (
    <>
      <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')} disabled={!canSave}
        title={canSave ? `Save (${formatCombo('mod+s')})` : NO_RIGHT_TIP}>
        <span className="tb-i">💾</span> Save
      </button>
      {!isNew && (
        <button className="ca-btn" onClick={() => void doSaveAs()} disabled={!canSaveAsJob}
          title={canSaveAsJob ? 'Duplicate this job under a new Job ID' : NO_RIGHT_TIP}>
          <span className="tb-i tb-i-blue">📋</span> Save As
        </button>
      )}
      {!isNew && (
        <button className="ca-btn" onClick={() => void doSubmit()} disabled={submitted || !canSave}
          title={!canSave ? NO_RIGHT_TIP : submitted ? 'Already submitted' : 'Submit — status → Registration Submitted'}>
          <span className="tb-i tb-i-green">📨</span> Submit
        </button>
      )}
      <button className="ca-btn" onClick={() => void guardedCancel(onClose)} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
    </>
  );

  const stepArrows = () => (
    <>
      <button className="sr-navcirc" title={`Back (${formatCombo('alt+pageup')})`} aria-label="Back" disabled={step === 0}
        onClick={() => void goToStep(Math.max(0, step - 1) as StepIndex)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 5 8 12 15 19" />
        </svg>
      </button>
      <button className="sr-navcirc" title={`Next (${formatCombo('alt+pagedown')})`} aria-label="Next"
        disabled={step === 5 || (step === 4 && !currentJobId)}
        onClick={() => void goToStep(Math.min(5, step + 1) as StepIndex)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 5 16 12 9 19" />
        </svg>
      </button>
    </>
  );

  // Phase 2.3 — Sample Registration detail keys (CLAUDE.md standing rule 14,
  // §18.6, ADR-0013).  This is a BESPOKE detail (not the shared SettingsDetailPage),
  // so it registers its OWN Save trio + Cancel + record prev/next, mirroring the
  // action-bar buttons.  Esc is allowInInput:false so it never discards edits while
  // typing; the store already yields Esc to any open dialog.
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'Sample Registration', order: 1, disabled: !canSave, run: () => { void doSave('stay'); } },
    // Save and Close / Save and New buttons + hotkeys removed (user request
    // 2026-09-24) — the action bar is now Save · Save As · Submit · Cancel.
    // Alt+U Submit — the documented workflow key (Keyboard Shortcuts Manual v3 §4:
    // "sUbmit"); mirrors the Submit button and its disabled state.
    { combo: 'alt+u', label: 'Submit', group: 'Sample Registration', order: 4, disabled: submitted || !canSave, run: () => { void doSubmit(); } },
    // Alt+PgUp / Alt+PgDn — step Back / Next through the 6-step workflow
    // (Registration → Samples → Tests → Containers → Save → Submit); mirrors the
    // ‹ › step arrows + their disabled state.  Alt+←/→ stays record prev/next.
    { combo: 'alt+pageup', label: 'Previous step', group: 'Sample Registration', order: 5, disabled: step === 0, run: () => { void goToStep(Math.max(0, step - 1) as StepIndex); } },
    { combo: 'alt+pagedown', label: 'Next step', group: 'Sample Registration', order: 6, disabled: step === 5 || (step === 4 && !currentJobId), run: () => { void goToStep(Math.min(5, step + 1) as StepIndex); } },
    { combo: 'esc', label: 'Cancel', group: 'Sample Registration', order: 9, allowInInput: false, run: () => { void guardedCancel(onClose); } },
    { combo: 'alt+left', label: 'Previous record', group: 'Sample Registration', order: 7, disabled: !prevId, run: () => { if (prevId) void guardedExit(() => onNavigate(prevId)); } },
    { combo: 'alt+right', label: 'Next record', group: 'Sample Registration', order: 8, disabled: !nextId, run: () => { if (nextId) void guardedExit(() => onNavigate(nextId)); } },
  ]);

  // ── Focus the first field (Client) on open ────────────────────────────────
  // The list→detail transition is internal state, NOT a route/page change, so
  // AppShell's page-level focusFirstFormControl never fires for this detail and
  // the Client combo's [data-autofocus] is never honoured.  Focus it here — one
  // shot per mount (the page remounts per record via its `key`).  90 ms lets the
  // step body settle so we win over an embedded grid that grabbed a row.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (document.querySelector('[aria-modal="true"]')) return;   // a dialog owns focus
      const root = rootRef.current;
      if (!root) return;
      const ae = document.activeElement as HTMLElement | null;
      const typing = !!ae && root.contains(ae)
        && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable);
      if (typing) return;                          // don't yank a field the user already started in
      focusFirstFormControl(root);                 // honours [data-autofocus] → Client
    }, 90);
    return () => window.clearTimeout(id);
  }, []);

  return (
    /* `sr-detail` makes this long-form page scroll (`.bl-page` is built for list
       screens and would clip it); `sr-dlg` scopes the prototype's accent dialog
       header to every popup this view renders (quick-add, Received Date, SCC,
       Note, Item Charge, Image, Import, Project Notes) — AppDialog renders
       inline, so one class on the root covers them all without touching any
       other form. */
    <div className="bl-page sr-detail sr-dlg" ref={rootRef}>
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="sr-crumb">
            <a onClick={() => void guardedCancel(onHome)}>Home</a>
            <span className="sr-crumb-sep">›</span>
            <span>Sample Tracking</span>
            <span className="sr-crumb-sep">›</span>
            <a onClick={() => void guardedCancel(onClose)}>Sample Registration</a>
            <span className="sr-crumb-sep">›</span>
            <a onClick={() => void goToStep(0)}><b>{recordLabel}</b></a>
            {step > 0 && <><span className="sr-crumb-sep">›</span><b>{STEPS[step]}</b></>}
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Audit moved to the top toolbar as a button (user request
                2026-09-24) — no title-row audit chip here anymore. */}
            <FormTitle
              pageId="sampreg"
              title={currentJobId ? `Sample Registration — ${currentJobId}` : 'Sample Registration'}
            />
            {/* Job chip — prototype `#dJobChip`: "⏱ Draft — not yet saved" on a new
                record, "⏱ Job {id} · saved" once it has an id.  The tooltip carries
                the honest caveat: nothing reaches the server in this design pass. */}
            <span className="loagt sr-jobchip"
              title={currentJobId ? `Job ${currentJobId} persisted via the save endpoint.` : 'Draft — save to create a Job ID.'}>
              ⏱ {currentJobId ? `Job ${currentJobId} · saved` : 'Draft — not yet saved'}
            </span>
            {/* Project Notes — prototype `.notelink`, opens the job's notes. */}
            <button className="notelink" onClick={() => setNotesOpen(true)}
              title="Project Notes for this job">
              📌 Project Notes ({tabs.notes.length})
            </button>
            {/* Top-right ‹ › = wizard STEP Back / Next (user request 2026-09-23,
                matching ALPACA Intelligence reference: Next → Samples page). Same
                guarded goToStep the flow-bar arrows use. */}
            {/* GoLive-EXACT record/step nav icons (user request 2026-09-24): the
                DevExpress Action_Navigation_History_Back / _Forward glossy circle
                arrows. The Forward's green is recolored to brand orange in CSS
                (hue-rotate; the white arrow is unaffected). */}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button className="sr-navimg" title="Back"
                disabled={step === 0} onClick={() => void goToStep(Math.max(0, step - 1) as StepIndex)}>
                <img src="/nav/sr-nav-back.png" alt="Back" />
              </button>
              <button className="sr-navimg sr-navimg-next" title="Next"
                disabled={step === 5 || (step === 4 && !currentJobId)}
                onClick={() => void goToStep(Math.min(5, step + 1) as StepIndex)}>
                <img src="/nav/sr-nav-fwd.png" alt="Next" />
              </button>
            </div>
          </div>

          {/* The ONE stepper — portalled into the AppShell top bar.  The prototype
              deliberately HIDES the step hint app-wide (`.hint{display:none!important}`
              — "not very useful once the user knows what to do, the stepper is good
              enough", 2026-07-18), so the banner is not rendered.  `stepHints` below
              keeps the wording ready if it is ever switched back on. */}
          <TopbarStepper
            steps={islandSteps}
            onStepClick={(id) => { void goToStep(Number(id) as StepIndex); }}
            isClickable={() => true}
          />
        </div>
      </div>

      <div className="bl-grid-area">
        {detailLoading && (
          <div className="sr-note" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <LoaderSymbol size={16} />
            Loading registration details…
          </div>
        )}
        {detailError && (
          <div className="sr-warnbox" style={{ marginBottom: 10 }}>
            Could not load this job from the server. You can refresh and try again.
          </div>
        )}
        {/* Action bar — form-specific LEFT, the Save set RIGHT (XAF canonical).
            Shown on the Sample Registration step (0) only. Hidden on Sample Login
            (step 1) and the focused Tests / Containers steps (2, 3), which carry
            their own Save changes / Cancel changes actions. */}
        {step === 0 && (
          <div className="sr-savebar">
            {/* Refresh — re-fetch this job from the server, discarding any
                unsaved local edits (legacy toolbar parity: btnRefresh). Only
                meaningful for a job that actually exists on the server. */}
            {!isNew && (
              <button className="ca-btn" title="Reload this job from the server"
                onClick={() => void guardedExit(() => setReloadTick(t => t + 1))}>
                <span className="tb-i tb-i-green">🔄</span> Refresh
              </button>
            )}
            {!isNew && (
              <button className="ca-btn" disabled={!canDeleteJob}
                title={canDeleteJob ? 'Delete this job' : NO_RIGHT_TIP}
                onClick={() => void doDeleteJob()}>
                <span className="tb-i tb-i-red">🗑</span> Delete
              </button>
            )}
            {/* Reports ▾ — items come from Custom Report Builder, never from code.
                Hidden entirely when the form has no assigned template, matching
                GoLive's invisible ReportsAction. */}
            {reportItems.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}>
                  <span className="tb-i tb-i-blue">📄</span> Reports ▾
                </button>
                {reportsOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setReportsOpen(false)} />
                    <div className="sr-colfind-menu" style={{ top: 34, left: 0, minWidth: 170, zIndex: 41 }}>
                      {reportItems.map(r => (
                        <button key={r.id} className="sr-colfind-item" title={r.templateName}
                          onClick={() => void previewReport(r)}>{r.label}</button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* pH Check — opens the Sample Container pH Check popup dialog
                scoped to this job (XAF parity: btnPH_Execute). Gate on
                `currentJobId` (the LIVE id set after save), NOT the immutable
                `jobId` prop — a job created via "New" keeps jobId=null even after
                it is saved, so the prop check wrongly showed "Save the job first."
                on an already-saved job (user report 2026-10-05). The dialog itself
                already reads currentJobId. */}
            <button className="ca-btn" title="Open Sample Container pH Check"
              onClick={() => currentJobId ? setPhCheckOpen(true) : notify.info('Save the job first.')}>
              <span className="tb-i tb-i-teal">🧫</span> pH Check
            </button>
            {/* Import Quote — XAF parity: btnQuoteImportSamples (caption "Import Quote",
                tooltip "Import Quotes", Down_16x16), a header action beside pH Check
                active for users with write rights. Same handler as the Quote ID
                field's Import button (validates the Quote ID, opens Import Samples). */}
            <button className="ca-btn" title={canSave ? 'Import Quotes' : NO_RIGHT_TIP}
              disabled={!canSave} onClick={() => void handleImport('quote')}>
              <i className="ti ti-download tb-i-grey" /> Import Quote
            </button>
            {/* CoolerID — shown only for Pre-scheduled (Sampling) jobs.
                XAF parity: BtnCoolerId (active when IsSampling=true) →
                SampleBottleAllocation_ListView_CoolerId popup. */}
            {registrationType === 'Pre-scheduled' && (
              <button className="ca-btn" title="Edit CoolerID per bottle"
                onClick={() => jobOid ? setCoolerIdOpen(true) : notify.info('Save the job first.')}>
                <span className="tb-i tb-i-blue">📦</span> CoolerID
              </button>
            )}
            {!isNew && !/^pending\s*(submission|submit)/i.test(header.status) && (
              <button className="ca-btn" onClick={doRecalculateInvoice}
                disabled={!canSave || header.signoffStatus === 0}
                title={!canSave ? NO_RIGHT_TIP
                  : header.signoffStatus === 0 ? 'Available after the job is submitted' : undefined}>
                <span className="tb-i tb-i-blue">🧮</span> Recalculate Invoice
              </button>
            )}
            {/* Generic Sample Label — legacy `btnPrintBarcodeLabel`
                (SampleRegistrationViewController line 295), shown once the job
                has been saved and carries a Job ID. */}
            {canPrintBarcodeLabel && (
              <button className="ca-btn" title="Generic Sample Label"
                onClick={() => setLabelOpen(true)}>
                <i className="ti ti-barcode tb-i-grey" /> Generic Sample Label
              </button>
            )}
            {/* Reset Job ID — prototype `↺ Reset Job ID`: clears the minted id so
                the record goes back to Autogenerate / Pending Saving. */}
            <button className="ca-btn" title={canSave ? 'Reset Job ID' : NO_RIGHT_TIP}
              disabled={!canSave} onClick={() => void resetJobId()}>
              <i className="ti ti-rotate tb-i-grey" /> Reset Job ID
            </button>
            {/* Audit Trail — at the END of the top-left form-specific buttons
                (user request 2026-09-24), config-gated shared button for the
                Sample Registration record. */}
            <AuditTrailButton formName={SAMPLE_REGISTRATION_FORM}
                              formNames={SAMPLE_REGISTRATION_AUDIT_FORMS}
                              sourceId={jobOid ?? undefined} />
            {/* Save As + Submit moved to the right group NEXT to Save (user
                request 2026-09-24) — see saveTrio(). */}
            <span className="sr-savebar-spacer" />
            {saveTrio()}
            {/* Step Back/Next arrows removed from the toolbar (user request
                2026-09-23) — step navigation now via the clickable top stepper
                chips + the flow-bar CTAs. */}
          </div>
        )}

        {/* ── Step bodies ─────────────────────────────────────────────────── */}
        {readOnly && (
          <div className="sr-note" style={{ marginBottom: 10 }}>
            <i className="ti ti-lock" /> Read-only — your access level does not allow editing this registration.
          </div>
        )}

        {step === 0 && (
          <>
            {/* Read-only (no Write / Create right): the header fields are made inert
                (not focusable / clickable) so nothing can be edited — ALPACA opens
                such a record in View mode. display:contents keeps the layout intact. */}
            <div style={{ display: 'contents' }} {...inertProps(readOnly)}>
              <RegistrationForm
                header={header}
                patch={patch}
                lookups={formLookups}
                onNewLookup={openNewLookup}
                onImport={handleImport}
                onWarn={m => notify.warning(m)}
                flash={flash}
                triedSave={triedSave}
                statusNameMap={statusNameMap}
                savedJob={!!jobOid}
                testCount={counts.tests}
                sampleCount={counts.samples}
              />
            </div>
            <RegistrationTabs
              readOnly={readOnly}
              onEnsureJob={ensureJobId}
              header={header}
              jobId={currentJobId ?? ''}
              jobOid={jobOid}
              user={user}
              now={nowLocal}
              tabs={tabs}
              setTabs={setTabs}
              templateTests={templateTests}
              customRequest={customRequest}
              setCustomRequest={setCustomRequest}
              tatHoursMap={tatHoursMap}
              holidaySet={holidaySet}
              registrationType={registrationType}
              lookups={formLookups}
              onWarn={m => notify.warning(m)}
              onInfo={m => notify.info(m)}
              onSuccess={m => notify.success(m)}
              confirmDelete={(what, run) =>
                confirmAction('Delete', `Delete ${what}? This cannot be undone.`, run, true)}
            />
          </>
        )}

        {(step === 1 || step === 2 || step === 3) && (
          <SampleLoginView
            readOnly={readOnly}
            jobId={currentJobId ?? ''}
            jobOid={jobOid}
            primaryMatrixOid={
              // Resolve the first selected matrix name → OID for New Mode config fetch.
              (header.matrices[0] != null
                ? (formLookups.matrix?.oidByLabel?.[header.matrices[0]] ?? null)
                : null)
            }
            clientOid={header.clientOid ?? null}
            samples={samples}
            setSamples={setSamples}
            tab={slTab}
            setTab={t => {
              setSlTab(t);
              setStep(t === 'testview' ? 2 : t === 'containers' ? 3 : 1);
            }}
            active={active}
            setActive={setActive}
            onBackToRegistration={() => void goToStep(0)}
            onWarn={m => notify.warning(m)}
            onInfo={m => notify.info(m)}
            onSuccess={m => notify.success(m)}
            confirmAction={confirmAction}
            // Refresh ONLY the sample rows after a delete / reanalysis. A full header
            // reload (reloadTick) overwrote unsaved header edits — e.g. a Test just
            // picked — so it vanished on return to Registration (bug 6, 2026-10-16).
            onServerReload={() => {
              if (!jobOid) return;
              void api.get(`/api/v1/sample-registration/${encodeURIComponent(jobOid)}/sample-logins`)
                .then(res => {
                  const mapped = mapSampleLoginsPayload((res.data as ApiSuccess<SampleLoginsDto | null>).data);
                  serverSamplesRef.current = mapped;
                  setSamples(mapped);
                  if (mapped.length > 0) setHeader(h => ({ ...h, noOfSamples: String(mapped.length) }));
                })
                .catch(() => setReloadTick(t => t + 1));
            }}
            onNewCollector={() => { setNewLookupKind('collector'); setNewLookupField(null); }}
            // A collector added via the Samples toolbar "Add Collector" must appear
            // in the grid's Collector dropdown (which reads the grid config, not the
            // header `lookups`) and be selected into the active row — user report
            // 2026-10-05. SampleLoginView consumes this then clears it.
            newCollector={pendingGridCollector}
            onCollectorConsumed={() => setPendingGridCollector(null)}
            actions={
              <>
                <button className="ca-btn ca-btn-solid" disabled={!canSave} title={canSave ? undefined : NO_RIGHT_TIP}
                  onClick={() => void doSave('stay')}>Save</button>
                <button className="ca-btn" disabled={!canSave} title={canSave ? undefined : NO_RIGHT_TIP}
                  onClick={() => void doSave('close')}>Save and Close</button>
                <button className="ca-btn" onClick={() => void guardedCancel(onClose)}>Cancel</button>
              </>
            }
            testRows={formLookups.test?.rows ?? []}
            groupChildren={groupChildren}
            matrixMap={matrixMap}
            collectorMap={collectorMap}
            headerCollectorNames={header.collector}
            receivedDates={tabs.receivedDates}
            unitOptions={unitNames}
            employeeOptions={employeeNames}
            recordDuplicateJustifications={recordDuplicateJustifications}
            justifiedParams={justifiedParams}
          />
        )}

        {step === 4 && (
          <section className="sr-sec">
            <h3 className="sr-sec-h"><span className="sr-sec-ico">💾</span> Save</h3>
            <div className="sr-sec-b">
              <div className="sr-savebar" style={{ margin: '0 0 12px' }}>
                <span className="sr-savebar-spacer" />
                {saveTrio()}
              </div>
              <SummaryBlock header={header} jobId={currentJobId} counts={counts} charge={charge} />
              <p className="sr-note" style={{ marginTop: 10 }}>
                Click <b>Save</b> to mint the Job ID (status <b>Pending Submission</b>), then continue
                to <b>Submit</b>.
              </p>
            </div>
          </section>
        )}

        {step === 5 && (
          <section className="sr-sec">
            <h3 className="sr-sec-h"><span className="sr-sec-ico">📨</span> Submit</h3>
            <div className="sr-sec-b">
              <div className="sr-savebar" style={{ margin: '0 0 12px' }}>
                <span className="sr-savebar-spacer" />
                <button className="ca-btn ca-btn-solid" disabled={!currentJobId || submitted || !canSave}
                  title={!canSave ? NO_RIGHT_TIP : currentJobId ? undefined : 'Save the registration first'}
                  onClick={() => void doSubmit()}>
                  <span className="tb-i">📨</span> Submit
                </button>
              </div>
              {currentJobId ? (
                <p className="sr-note">
                  Job <b>{currentJobId}</b> is saved (status <b>{header.status}</b>). Click <b>Submit</b> to
                  set it <b>Submitted</b> and release it to Sample Receipt Notification.
                </p>
              ) : (
                /* Prototype-exact amber note on the Submit step (this one IS shown —
                   it is a step-body warning, not the hidden hint banner). */
                <div className="sr-warnbox">
                  ⚠ Not saved yet — go back to <b>Save</b> first. Submit is disabled until the Job ID is created.
                </div>
              )}
              <SummaryBlock header={header} jobId={currentJobId} counts={counts} charge={charge} />
            </div>
          </section>
        )}

        {/* ── Flow bar — step CTAs with live counts, Back / Next on the right ── */}
        {/* Hidden on Sample Login / Test View / Containers (steps 1-3) to give
            grids more vertical space; shown on Registration + Save + Submit. */}
        {(step === 0 || step >= 4) && <div className="sr-flowbar">
          {([[1, '🧪 Samples', flowBarCounts.samples], [2, '🔬 Tests', flowBarCounts.tests], [3, '📦 Containers', flowBarCounts.containers]] as [StepIndex, string, number][])
            .map(([target, label, count]) => (
              <button key={label} className={`sr-flowbtn${step + 1 === target ? ' sr-next' : ''}`}
                onClick={() => void goToStep(target)}>
                {label} <span className="sr-cnt">{count}</span>
              </button>
            ))}
          <span style={{ flex: 1 }} />
          {stepArrows()}
        </div>}

        {/* The bottom Save bar repeats only on the long Registration step. */}
        {step === 0 && (
          <div className="sr-savebar" style={{ justifyContent: 'flex-end' }}>
            {saveTrio()}
          </div>
        )}
      </div>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      {/* Generic Sample Label — legacy PrintBarcode_DashboardView popup. */}
      <GenericSampleLabelDialog
        open={labelOpen}
        jobId={currentJobId ?? ''}
        groupVisualMatrix={header.matrices[0] ?? ''}
        firstSampleVisualMatrix={firstSampleMatrix}
        matrixOptions={labelMatrixOptions}
        onClose={() => setLabelOpen(false)}
      />

      {/* Sample Container pH Check — legacy btnPH_Execute popup. */}
      <SampleContainerPhCheckDialog
        open={phCheckOpen}
        jobId={currentJobId ?? ''}
        onClose={() => setPhCheckOpen(false)}
      />

      {/* CoolerID — legacy BtnCoolerId_Execute popup (Pre-scheduled jobs only). */}
      <CoolerIdDialog
        open={coolerIdOpen}
        jobId={currentJobId ?? ''}
        jobOid={jobOid ?? ''}
        onClose={() => setCoolerIdOpen(false)}
      />

      <NewLookupDialog
        kind={newLookupKind}
        clientName={header.client}
        clientOid={header.clientOid}
        onClose={() => { setNewLookupKind(null); setNewLookupField(null); }}
        onAdd={addLookupRow}
        onWarn={m => notify.warning(m)}
        onSuccess={m => notify.success(m)}
      />

      {importKind === 'coc' && (
        <CocPreLogImportDialog
          jobOid={jobOid}
          // header.projectId / header.client hold the DISPLAY value the user
          // picked; resolve to the FK Oid the same way persist() does.
          projectOid={header.projectOid || null}
          clientOid={header.clientOid || null}
          ensureJobOid={ensureJobForCoc}
          onClose={() => setImportKind(null)}
          onImported={(summary) => {
            setImportKind(null);
            setReloadTick(t => t + 1);
            // Re-hydrate the sample rows from what the import PERSISTED. For a brand-new
            // job the reload effect above is keyed on the (still-null) jobId prop and
            // returns early, so the in-memory samples stayed whatever was generated from
            // the header Test list — every test with ALL parameters — instead of the
            // Pre-Log's customised selection (user report 2026-10-09).
            const importedOid = jobOid || lastSavedOidRef.current;
            if (importedOid) {
              void api.get(`/api/v1/sample-registration/${encodeURIComponent(importedOid)}/sample-logins`)
                .then(r => {
                  const mapped = mapSampleLoginsPayload((r.data as ApiSuccess<SampleLoginsDto | null>).data);
                  serverSamplesRef.current = mapped;
                  if (mapped.length > 0) {
                    setSamples(mapped);
                    setHeader(h => ({ ...h, noOfSamples: String(mapped.length) }));
                  }
                })
                .catch(() => { /* non-fatal — the reload effect / next open reads the same rows */ });
            }
            notify.success(
              `Pre-Log ${summary.cocId} imported — ${summary.samplesCount} sample(s), ` +
              `${summary.testsCount} test(s), ${summary.bottlesCount} bottle(s).`);
          }}
          onWarn={m => notify.warning(m)}
        />
      )}
      {importKind === 'quote' && (
        <QuoteImportDialog
          jobOid={jobOid}
          quoteOid={formLookups.quote?.oidByLabel?.[header.quoteId] ?? null}
          quoteDisplay={header.quoteId}
          matrixKey={header.matrices.join('; ')}
          onClose={() => setImportKind(null)}
          onImported={(summary) => {
            setImportKind(null);
            setReloadTick(t => t + 1);
            notify.success(
              `Quote imported — ${summary.samplesCount} sample(s), ` +
              `${summary.testsCount} test(s), ${summary.bottlesCount} bottle(s), ` +
              `${summary.itemChargesCount} item charge(s).`);
          }}
          onWarn={m => notify.warning(m)}
        />
      )}

      {/* Project Notes — XAF-style two-level popup (list → detail).
          Matches ProjectNotesViewController: 1300×600 list with toolbar,
          750×600 detail with Title / Date / Author / Sort / rich-text Note. */}
      {notesOpen && (
        <ProjectNotesDialog
          jobId={currentJobId ?? ''}
          user={user}
          now={nowLocal}
          notes={tabs.notes}
          setNotes={notes => setTabs({ ...tabs, notes })}
          onClose={() => setNotesOpen(false)}
          onWarn={m => notify.warning(m)}
          confirmDelete={(what, run) =>
            confirmAction('Delete', `Delete ${what}? This cannot be undone.`, run, true)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Which header field each lookup's quick-add feeds back into. */
const FIELD_FOR_LOOKUP: Partial<Record<SrLookupKey, keyof SrHeader>> = {
  client: 'client', contact: 'clientContact', invoicecontact: 'invoiceContact', project: 'projectId',
  category: 'projectCategory', pm: 'projectManager', payment: 'paymentStatus',
  inact: 'jobInactivation', permit: 'permit', cocid: 'cocSource',
  relinq: 'relinquishedBy', collector: 'collector',
};

/** Peel the server-side error message out of an axios error (or any thrown
 *  Error) so the user sees the real cause instead of a generic banner. Mirrors
 *  the pattern used by QcBatchDetailPage / PrepBatchDetailPage. */
// ─────────────────────────────────────────────────────────────────────────────
// ProjectNotesDialog — XAF-style two-level popup (list → detail)
// Matches ProjectNotesViewController: 1300×600 list with toolbar,
// 750×600 detail with Title / Date / Author / Sort / rich-text Note.
// ─────────────────────────────────────────────────────────────────────────────
function ProjectNotesDialog({
  jobId, user, now, notes, setNotes, onClose, onWarn, confirmDelete,
}: {
  jobId: string;
  user: string;
  now: string;
  notes: SrNote[];
  setNotes: (notes: SrNote[]) => void;
  onClose: () => void;
  onWarn: (msg: string) => void;
  confirmDelete: (label: string, action: () => void) => void;
}) {
  const [editing, setEditing] = useState<SrNote | null>(null);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);

  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

  const notAuthor = (n: SrNote) => n.author !== user;

  const blankNote = (): SrNote => ({
    key: nextKey('note'), oid: '', title: '', author: user, authorOid: '',
    date: now, sort: 0, html: '', plainText: '',
    sourceId: jobId, noteSource: 'Sample Registration',
  });

  const save = () => {
    if (!editing) return;
    if (!editing.title.trim()) { onWarn('Enter the Title.'); return; }
    const html = bodyRef.current?.innerHTML ?? editing.html;
    const plain = stripHtml(html);
    if (!plain) { onWarn('Enter the Note text.'); return; }
    const rec: SrNote = { ...editing, html, plainText: plain };
    const exists = notes.some(n => n.key === rec.key);
    setNotes(exists ? notes.map(n => (n.key === rec.key ? rec : n)) : [...notes, rec]);
    setEditing(null);
  };

  // Per-row edit — opens THIS note's editor from the inline pencil in the Edit
  // column (no single toolbar Edit button; the user edits the exact note without
  // ticking it first).  Author-only: a non-author gets the ALPACA banner and the
  // row shows no pencil, so this is the double-guard (§14a Rule C).
  const editRow = (n: SrNote) => {
    if (notAuthor(n)) { onWarn('You are not authorized to modify a note created by another author.'); return; }
    setEditing({ ...n });
  };

  const tryDelete = () => {
    const picked = notes.filter(n => sel.has(n.key));
    if (picked.some(notAuthor)) {
      onWarn('You are not authorized to modify a note created by another author.');
      return;
    }
    confirmDelete(`${sel.size} note(s)`, () => {
      setNotes(notes.filter(n => !sel.has(n.key)));
      setSel(new Set());
    });
  };

  const cols: AppColumn<SrNote>[] = [
    // Per-row Edit — inline pencil in a dedicated "Edit" column (replaces the
    // single toolbar Edit button; edit is a ROW action). Author-only: a
    // non-author sees a dash, not the pencil (the editRow guard still enforces).
    {
      field: '__edit', header: 'Edit', width: 52, align: 'center',
      sortable: false, filterable: false, noExport: true,
      render: n => (notAuthor(n)
        ? <span title="Only the author can edit this note." style={{ color: 'var(--ai-muted)' }}>—</span>
        : <button className="sr-edit-cell" title="Edit note"
            onClick={e => { e.stopPropagation(); editRow(n); }}>
            <i className="ti ti-edit" />
          </button>),
    },
    { field: 'title', header: 'Title', width: 200 },
    { field: 'plainText', header: 'Note', width: 360, text: r => r.plainText || stripHtml(r.html) },
    { field: 'author', header: 'Author', width: 140 },
    { field: 'date', header: 'Date', width: 170, text: r => fmtLocalDateTime(r.date) },
    { field: 'sort', header: 'Sort', type: 'number', width: 70 },
  ];

  return (
    <>
      <AppDialog open
        title={jobId ? `Project Notes — ${jobId}` : 'Project Notes'}
        icon="ti-note" width={1300} className="sr-dlg"
        onClose={onClose}
        footer={<button className="ca-btn" onClick={onClose}>Close</button>}>
        <AppToolbar
          onNew={() => setEditing(blankNote())}
          onDelete={tryDelete}
          deleteDisabled={sel.size === 0}
        />
        <AppDataGrid<SrNote>
          rows={notes} columns={cols} getRowId={r => r.key}
          selectable selectedIds={sel} onSelectionChange={setSel}
          onRowDoubleClick={editRow}
          defaultPageSize={20} emptyText="No notes yet."
        />
      </AppDialog>

      {editing && (
        <AppDialog open title="Note"
          icon="ti-note" width={750} className="sr-dlg"
          onClose={() => setEditing(null)}
          footer={<>
            <button className="ca-btn" onClick={() => setEditing(null)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save}>OK</button>
          </>}>
          <div className="sr-note-grid">
            <label className="req">Title:</label>
            <div className="sr-note-val">
              {!editing.title.trim() && <span className="sr-val-icon" title="Required">⊘</span>}
              <input value={editing.title} className={editing.title.trim() ? undefined : 'sr-req-empty'}
                onChange={e => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <label>Business Object:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />

            <label>Source ID:</label>
            <input value={editing.sourceId || jobId} readOnly />
            <label>Author:</label>
            <input value={editing.author} readOnly />

            <label>Name Source:</label>
            <input value={editing.noteSource || 'Sample Registration'} readOnly />
            <label>Date:</label>
            <input value={fmtLocalDateTime(editing.date)} readOnly />
          </div>
          <div className="sr-rte-wrap">
            <span className="sr-rte-tab">Note</span>
            <div className="sr-rte">
              <div className="sr-rte-bar">
                <button className="sr-rte-btn" onClick={() => document.execCommand('undo')} title="Undo"><i className="ti ti-arrow-back-up" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('redo')} title="Redo"><i className="ti ti-arrow-forward-up" /></button>
                <span className="sr-rte-sep" />
                <button className="sr-rte-btn" onClick={() => document.execCommand('copy')} title="Copy"><i className="ti ti-copy" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('cut')} title="Cut"><i className="ti ti-cut" /></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('paste')} title="Paste"><i className="ti ti-clipboard" /></button>
                <span className="sr-rte-sep" />
                <select className="sr-rte-dd sr-rte-font" defaultValue="Calibri"
                  onChange={e => { document.execCommand('fontName', false, e.target.value); }}>
                  <option>Calibri</option><option>Arial</option><option>Times New Roman</option>
                  <option>Courier New</option><option>Georgia</option><option>Verdana</option>
                </select>
                <select className="sr-rte-dd" defaultValue=""
                  onChange={e => { if (e.target.value) document.execCommand('fontSize', false, e.target.value); e.target.value = ''; }}>
                  <option value="" disabled>Font Size</option>
                  <option value="1">8</option><option value="2">10</option><option value="3">12</option>
                  <option value="4">14</option><option value="5">18</option><option value="6">24</option><option value="7">36</option>
                </select>
                <select className="sr-rte-dd" defaultValue=""
                  onChange={e => { if (e.target.value) document.execCommand('formatBlock', false, e.target.value); e.target.value = ''; }}>
                  <option value="" disabled>Paragraph</option>
                  <option value="p">Normal</option><option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option><option value="h3">Heading 3</option>
                  <option value="blockquote">Quote</option><option value="pre">Code</option>
                </select>
                <button className="sr-rte-btn" onClick={() => document.execCommand('bold')} title="Bold"><b>B</b></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('italic')} title="Italic"><i>I</i></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('underline')} title="Underline"><u>U</u></button>
                <button className="sr-rte-btn" onClick={() => document.execCommand('strikeThrough')} title="Strikethrough"><s>S</s></button>
              </div>
              <div ref={bodyRef} className="sr-rte-body" contentEditable suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: editing.html }} />
            </div>
          </div>
        </AppDialog>
      )}
    </>
  );
}

function apiErrMsg(e: unknown, fallback: string): string {
  const er = e as {
    response?: { data?: { error?: { message?: string } } };
    message?: string;
  };
  return er.response?.data?.error?.message ?? er.message ?? fallback;
}


function cloneLookups(): Record<SrLookupKey, SrLookup> {
  const out = {} as Record<SrLookupKey, SrLookup>;
  for (const [k, v] of Object.entries(LOOKUPS) as [SrLookupKey, SrLookup][]) {
    out[k] = { cols: [...v.cols], rows: v.rows.map(r => [...r]) };
  }
  return out;
}

function splitList(v: string): string[] {
  return v
    .split(';')
    .map(s => s.trim())
    .filter(Boolean);
}

function mapDetailToHeader(
  d: SampleRegistrationDetailDto,
  registrationType: string,
  now: string,
  user: string,
): SrHeader {
  return {
    ...emptyHeader(registrationType, now, user),
    jobId: d.jobId ?? '',
    // Received Date is a datetime-local input — needs YYYY-MM-DDTHH:mm, so use
    // toDateTimeInput (toDateInput returns date-only, which the input rejects →
    // blank field on edit).
    receivedDate: toDateTimeInput(d.receivedDate) || now,
    receivedBy: d.receivedBy ?? '',
    createdBy: d.createdBy ?? user,
    client: d.client ?? '',
    clientOid: d.clientOid ?? '',
    clientAddress: d.clientAddress ?? '',
    clientPhone: d.clientPhone ?? '',
    clientContact: d.clientContact ?? '',
    accountNumber: d.accountNumber ?? '',
    quoteId: d.quoteId ?? '',
    poNumber: d.poNumber ?? '',
    paymentStatus: d.paymentStatus ?? '',
    manifestNumber: d.manifestNumber === null || d.manifestNumber === undefined ? '' : String(d.manifestNumber),
    projectManager: d.projectManager ?? '',
    dateExpect: toDateInput(d.dateExpect),
    relinquishedBy: splitList(d.relinquishedBy ?? ''),
    collector: splitList(d.collector ?? ''),
    dateCollected: toDateInput(d.dateCollected),
    timeCollected: d.timeCollected ?? '',
    projectId: d.projectId ?? '',
    projectOid: d.projectOid ?? '',
    projectName: d.projectName ?? '',
    projectLocation: d.projectLocation ?? '',
    permit: d.permit ?? '',
    projectCategory: d.projectCategory ?? '',
    cocId: d.cocId ?? '',
    invoiceContact: d.invoiceContact ?? '',
    registrationType: d.registrationType || registrationType,
    jobInactivation: d.jobInactivation ?? '',
    cancelJob: !!d.cancelJob,
    cancelStatus: d.cancelStatus ?? 0,
    signoffStatus: d.signoffStatus ?? 0,
    cocSource: d.cocSource ?? '',
    status: d.statusName ?? 'Pending Submission',
    matrices: splitList(d.matrices ?? ''),
    testTemplate: splitList(d.testTemplate ?? ''),
    testTemplateMethods: {},
    sampleCategory: splitList(d.sampleCategory ?? ''),
    coolerId: d.coolerId ?? '',
    noOfSamples: d.noOfSamples && d.noOfSamples > 0 ? String(d.noOfSamples) : '1',
    remark: d.remark ?? '',
    tat: d.tat ?? '',
    dueDate: toDateTimeInput(d.dueDate),   // the Due Date control is a datetime-local; a date-only value renders blank (bug 8)
    reportTemplate: d.reportTemplate ?? '',
    eddTemplate: d.eddTemplate ?? '',
    // API returns the EDDFormat enum int; convert back to the display label so
    // the picker shows "Export to CSV", not "3".
    eddFormat: eddFormatFromInt(d.eddFormat),
    reportDeliveryAddress: d.reportDeliveryAddress ?? '',
    reportDeliveryMethod: d.reportDeliveryMethod ?? '',
  };
}

export function mapLookupRows(rows: SampleRegistrationLookupRowDto[]): Record<SrLookupKey, SrLookup> {
  const next = cloneLookups();
  // Every lookup keeps a display→Oid map (populated from Col5 of the SP payload)
  // so persist() can resolve the FK at Save time — the field stores the display
  // for UX, the map holds the Oid the API actually needs.
  for (const k of Object.keys(next) as SrLookupKey[]) {
    next[k].oidByLabel = {};
  }
  const seenRelinq = new Set<string>();
  for (const row of rows) {
    const key = row.lookupKey as SrLookupKey;
    if (!(key in next)) continue;
    if (key === 'edd') continue;
    // Permit's STORED value is PermitName (Col3), not Col1 (which is the ID) —
    // the grid shows ID·PermitType·Name·Number but Save/Get key on the name, so
    // oidByLabel must be keyed by Col3 (user request 2026-09-24).
    const label = key === 'permit' ? (row.col3 ?? '') : (row.col1 ?? '');
    const oid   = row.col5 ?? '';
    // Test lookup surfaces Test / Method / IsGroup / Component. Its selection VALUE is the
    // XAF composite "TestName|MethodNumber" (col1|col2) so two same-named tests
    // with different methods are distinct and each maps to its own TestMethod
    // Oid. oidByLabel is therefore keyed by the composite, not the bare name.
    if (key === 'test') {
      const method   = row.col2 ?? '';
      const composite = method ? `${label}|${method}` : label;
      if (composite && oid) next.test.oidByLabel![composite] = oid;
      next.test.rows.push([label, method, row.col3 ?? '', row.col4 ?? '', row.col6 ?? '', oid]);
      continue;
    }
    // Relinquished By is stored and resolved BY NAME (display → Oid), so rows
    // sharing a name (FedEx / FedEx / FedEX in dbo.BroughtBy) are
    // indistinguishable to the user and to Save — list each name once.
    if (key === 'relinq') {
      const norm = label.trim().toLowerCase();
      if (!norm || seenRelinq.has(norm)) continue;
      seenRelinq.add(norm);
    }
    if (label && oid) {
      next[key].oidByLabel![label] = oid;
    }
    const width = next[key].cols.length;
    const values = [row.col1, row.col2, row.col3, row.col4];
    const display = values.slice(0, width);
    // Client names can collide (e.g. two "City of Houston"). Carry the exact
    // Oid as a HIDDEN trailing element (the picker clips rendering to cols.length)
    // so applyPick captures THIS row's client — not an ambiguous name lookup —
    // which the client-scoped Project / Collector / Contact / Quote lists need.
    // Project IDs repeat across sites ("Project 067" → many names), so the
    // label map can't tell them apart either — carry the Oid on the row too.
    if ((key === 'client' || key === 'project') && oid) display.push(oid);
    next[key].rows.push(display);
  }
  // TAT dropdown: order by DURATION (hours → days), not alphabetically, so it
  // reads 2 hours · 4 hours · 8 hours · 1 Day · 2 Days · … like G5 (user request
  // 2026-09-26). The SP's global ORDER BY sorts by name, which interleaves
  // "10 Days" before "2 Days"; re-sort here by the parsed duration.
  next.tat.rows.sort((a, b) => tatDurationHours(a[0] ?? '') - tatDurationHours(b[0] ?? ''));
  return next;
}

/** Parse a TAT label ("2 hours", "1 Day", "10 Days", …) to hours for sorting.
 *  Unknown formats sort last. */
function tatDurationHours(s: string): number {
  const m = /(\d+(?:\.\d+)?)\s*(hour|hr|day|week|month)/i.exec(s.trim());
  if (!m) return Number.MAX_SAFE_INTEGER;
  const n = parseFloat(m[1]!);
  const u = m[2]!.toLowerCase();
  const mult = u.startsWith('hour') || u.startsWith('hr') ? 1
    : u.startsWith('day') ? 24
    : u.startsWith('week') ? 168
    : u.startsWith('month') ? 720
    : 1;
  return n * mult;
}

function seedHeader(
  jobId: string | null, registrationType: string, now: string, user: string,
  seed?: SampleRegistrationDetailPageProps['seed'],
): SrHeader {
  const h = emptyHeader(registrationType, now, user);
  if (!jobId) return h;
  return {
    ...h,
    jobId,
    status: seed?.status ?? 'Pending Submission',
    client: seed?.client ?? '',
    projectName: seed?.projectName ?? '',
    receivedDate: seed?.receivedDate ?? now,
    dueDate: seed?.dueDate ? toDateTimeInput(seed.dueDate) : '',
  };
}

/** Returns an empty tab state.  The first received-date row is seeded via a
 *  `useEffect` once the user picks a matrix (XAF parity — see rdAutoSeededRef). */
function seedTabs(_now: string, _user: string): SrTabState {
  return emptyTabState();
}

function SummaryBlock({ header, jobId, counts, charge }: {
  header: SrHeader; jobId: string | null;
  counts: { samples: number; tests: number; containers: number };
  charge: ReturnType<typeof chargeRows>;
}) {
  const row = (label: string, value: string) => (
    <div className="sr-sum-row" key={label}><span>{label}</span><span>{value || '—'}</span></div>
  );
  return (
    <div className="sr-sum">
      <div>
        <div className="sr-sum-h">Registration Summary</div>
        {row('Job ID', jobId ?? 'Autogenerate')}
        {row('Client', header.client)}
        {row('Project ID', header.projectId)}
        {row('Sample Matrix', header.matrices.join('; '))}
        {row('Received Date', fmtLocalDateTime(header.receivedDate))}
        {row('Due Date', fmtLocalDate(header.dueDate))}
        {row('Samples', String(counts.samples))}
        {row('Test assignments', String(counts.tests))}
        {row('Status', header.status)}
      </div>
      <div>
        <div className="sr-sum-h">Analysis Charge</div>
        <table className="sr-charge">
          <thead>
            <tr>
              <th>Test</th><th>Method</th><th className="sr-num">Qty</th>
              <th className="sr-num">Unit Price</th><th className="sr-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {charge.rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', fontStyle: 'italic', color: '#9aa1ad', padding: 16 }}>
                No tests.
              </td></tr>
            ) : charge.rows.map(r => (
              <tr key={r.test}>
                <td>{r.test}</td><td>{r.method}</td>
                <td className="sr-num">{r.qty}</td>
                <td className="sr-num">${r.unitPrice.toFixed(2)}</td>
                <td className="sr-num">${r.amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={4} className="sr-num">Total</td><td className="sr-num">${charge.total.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Quote Import — "Import Samples" from CRMQuotes ─────────────────────────

interface QuoteAnalysisLineDto {
  oid: string;
  priceCode: string | null;
  testName: string | null;
  methodNumber: string | null;
  matrixName: string | null;
  componentName: string | null;
  parameterName: string | null;
  qty: number;
  unitPrice: number;
  amount: number;
  isGroup: boolean;
}

interface QuoteItemChargeLineDto {
  oid: string;
  description: string | null;
  itemName: string | null;
  qty: number;
  unitPrice: number;
  amount: number;
}

interface QuoteLinesResultDto {
  analysisLines: QuoteAnalysisLineDto[];
  itemChargeLines: QuoteItemChargeLineDto[];
}

export interface QuoteImportSummary {
  samplesCount: number;
  testsCount: number;
  bottlesCount: number;
  itemChargesCount: number;
}

function QuoteImportDialog({
  jobOid, quoteOid, quoteDisplay, matrixKey, onClose, onImported, onWarn,
}: {
  jobOid: string | null;
  quoteOid: string | null;
  quoteDisplay: string;
  /** Sample Matrix chosen on the form ("; "-joined) — only its quote lines are offered. */
  matrixKey: string;
  onClose: () => void;
  onImported: (summary: QuoteImportSummary) => void;
  onWarn: (message: string) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [analysisLines, setAnalysisLines] = useState<QuoteAnalysisLineDto[]>([]);
  const [itemChargeLines, setItemChargeLines] = useState<QuoteItemChargeLineDto[]>([]);
  const [selAnalysis, setSelAnalysis] = useState<Set<string>>(new Set());
  const [selItemCharge, setSelItemCharge] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!quoteOid) { setLoading(false); return; }
    const ac = new AbortController();
    setLoading(true);
    void api.get('/api/v1/sample-registration/quote-lines', {
      params: { quoteOid }, signal: ac.signal,
    })
      .then(res => {
        const d = (res.data as ApiSuccess<QuoteLinesResultDto>).data;
        // A quote can span several matrices: offer only the tests of the matrix
        // picked on the form (bug 1, 2026-10-16). No matrix picked → everything.
        const wanted = new Set(matrixKey.split(';').map(m => m.trim().toLowerCase()).filter(Boolean));
        const lines = (d.analysisLines ?? []).filter(r =>
          wanted.size === 0 || wanted.has((r.matrixName ?? '').trim().toLowerCase()));
        setAnalysisLines(lines);
        setItemChargeLines(d.itemChargeLines ?? []);
        const allAn = new Set(lines.map(r => r.oid));
        const allIc = new Set((d.itemChargeLines ?? []).map(r => r.oid));
        setSelAnalysis(allAn);
        setSelItemCharge(allIc);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setAnalysisLines([]); setItemChargeLines([]);
        onWarn(e instanceof Error ? `Failed to load quote lines: ${e.message}` : 'Failed to load quote lines.');
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [quoteOid, matrixKey]);

  const toggleAnalysis = (oid: string) =>
    setSelAnalysis(s => { const n = new Set(s); n.has(oid) ? n.delete(oid) : n.add(oid); return n; });
  const toggleAllAnalysis = () =>
    setSelAnalysis(s => s.size === analysisLines.length ? new Set() : new Set(analysisLines.map(r => r.oid)));
  const toggleItemCharge = (oid: string) =>
    setSelItemCharge(s => { const n = new Set(s); n.has(oid) ? n.delete(oid) : n.add(oid); return n; });
  const toggleAllItemCharge = () =>
    setSelItemCharge(s => s.size === itemChargeLines.length ? new Set() : new Set(itemChargeLines.map(r => r.oid)));

  const doImport = async () => {
    if (!jobOid) { onWarn('Save the job before importing a Quote.'); return; }
    if (!quoteOid) { onWarn('Select a Quote ID on the form first.'); return; }
    if (selAnalysis.size === 0 && selItemCharge.size === 0) { onWarn('Select at least one line to import.'); return; }
    setImporting(true);
    try {
      const res = await api.post(
        `/api/v1/sample-registration/${encodeURIComponent(jobOid)}/import-quote`,
        {
          quoteOid,
          selectedAnalysisOids: [...selAnalysis],
          selectedItemChargeOids: [...selItemCharge],
        });
      const d = (res.data as ApiSuccess<QuoteImportSummary>).data;
      onImported(d);
    } catch (e: unknown) {
      const msg = (e && typeof e === 'object' && 'response' in e)
        ? ((e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Failed to import quote.')
        : (e instanceof Error ? e.message : 'Failed to import quote.');
      onWarn(msg);
    } finally {
      setImporting(false);
    }
  };

  const fmtPrice = (v: number) => `$${v.toFixed(2)}`;

  return (
    <AppDialog open width={900} icon="ti-download" title="Import Samples" onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={importing || (selAnalysis.size === 0 && selItemCharge.size === 0)}
          onClick={() => void doImport()}>
          {importing ? 'Importing…' : 'OK'}
        </button>
      </>}>
      {!quoteOid && <p style={{ padding: 12, color: 'var(--ai-muted)' }}>Select a Quote ID on the form first.</p>}
      {quoteOid && loading && <p style={{ padding: 12 }}>Loading…</p>}
      {quoteOid && !loading && (
        <>
          <p style={{ padding: '4px 12px', fontSize: 13, color: 'var(--ai-muted)' }}>
            Quote: <strong>{quoteDisplay || quoteOid}</strong>
          </p>

          {analysisLines.length > 0 && (
            <>
              <p style={{ padding: '8px 12px 2px', fontWeight: 600, fontSize: 13 }}>Analysis Pricing</p>
              <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4, margin: '0 12px' }}>
                <table className="sr-pick-table">
                  <thead><tr>
                    <th style={{ width: 32 }}><input type="checkbox" checked={selAnalysis.size === analysisLines.length} onChange={toggleAllAnalysis} /></th>
                    <th>Price Code</th><th>Test</th><th>Method</th><th>Matrix</th><th>Component</th><th>Qty</th><th>Unit Price</th><th>Amount</th>
                  </tr></thead>
                  <tbody>
                    {analysisLines.map(r => (
                      <tr key={r.oid} style={{ cursor: 'pointer' }} onClick={() => toggleAnalysis(r.oid)}>
                        <td><input type="checkbox" checked={selAnalysis.has(r.oid)} readOnly /></td>
                        <td>{r.priceCode}</td><td>{r.testName}{r.isGroup ? ' (G)' : ''}</td><td>{r.methodNumber}</td>
                        <td>{r.matrixName}</td><td>{r.componentName}</td><td>{r.qty}</td>
                        <td>{fmtPrice(r.unitPrice)}</td><td>{fmtPrice(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {itemChargeLines.length > 0 && (
            <>
              <p style={{ padding: '8px 12px 2px', fontWeight: 600, fontSize: 13 }}>Item Charges</p>
              <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4, margin: '0 12px 12px' }}>
                <table className="sr-pick-table">
                  <thead><tr>
                    <th style={{ width: 32 }}><input type="checkbox" checked={selItemCharge.size === itemChargeLines.length} onChange={toggleAllItemCharge} /></th>
                    <th>Item</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Amount</th>
                  </tr></thead>
                  <tbody>
                    {itemChargeLines.map(r => (
                      <tr key={r.oid} style={{ cursor: 'pointer' }} onClick={() => toggleItemCharge(r.oid)}>
                        <td><input type="checkbox" checked={selItemCharge.has(r.oid)} readOnly /></td>
                        <td>{r.itemName}</td><td>{r.description}</td><td>{r.qty}</td>
                        <td>{fmtPrice(r.unitPrice)}</td><td>{fmtPrice(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {analysisLines.length === 0 && itemChargeLines.length === 0 && (
            <p style={{ padding: 12, color: 'var(--ai-muted)' }}>
              {matrixKey
                ? `No analysis pricing or item charges found for this quote under the selected matrix (${matrixKey}).`
                : 'No analysis pricing or item charges found for this quote.'}
            </p>
          )}
        </>
      )}
    </AppDialog>
  );
}

// ── COC Import Stage 2 — "Import Pre-Log" ───────────────────────────────────

/** One dbo.COCSettings row from GET /sample-registration/coc-prelog. */
interface CocPreLogRowDto {
  oid: string;
  cocId: string | null;
  cocName: string | null;
  projectId: string | null;
  projectName: string | null;
  projectLocation: string | null;
  sampleMatrixNames: string | null;
  testNames: string | null;
  numberOfSample: number;
  retireDate: string | null;
}

/** Summary handed back to the parent on a successful import. */
export interface CocPreLogImportSummary {
  cocId: string;
  samplesCount: number;
  testsCount: number;
  bottlesCount: number;
}

/**
 * "Import Pre-Log" (COC Import Stage 2) — picks ONE dbo.COCSettings pre-log
 * record and copies it onto the current, already-saved job.  Legacy parity:
 * SampleRegistrationViewController.btnCOCImport_Execute — a client/project-
 * scoped picker, then POST .../import-coc-prelog does the actual copy
 * (header + samples + tests + bottles) server-side.
 */
function CocPreLogImportDialog({
  jobOid, projectOid, clientOid, onClose, onImported, onWarn, ensureJobOid,
}: {
  jobOid: string | null;
  projectOid: string | null;
  clientOid: string | null;
  onClose: () => void;
  onImported: (summary: CocPreLogImportSummary) => void;
  onWarn: (message: string) => void;
  /** For a brand-new (unsaved) job: create it first, applying the chosen
   *  pre-log's matrix, and return the new job Oid (legacy parity — no matrix
   *  prompt). */
  ensureJobOid: (matrixNames: string[]) => Promise<string>;
}) {
  const [rows, setRows] = useState<CocPreLogRowDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  // Client-side "Text to search…" box — legacy Import Pre-Log popup parity
  // (user request 2026-09-24). Matches any visible column.
  const [q, setQ] = useState('');
  // Per-column Excel-style filter (GoLive parity, user request 2026-09-24):
  // each header ▾ opens a distinct-value checklist; an empty set = no filter.
  const COC_COLS = [
    { key: 'cocId', label: 'COC_ID' },
    { key: 'cocName', label: 'COCName' },
    { key: 'projectId', label: 'ProjectID' },
    { key: 'projectName', label: 'ProjectName' },
    { key: 'projectLocation', label: 'ProjectLocation' },
    { key: 'sampleMatrixNames', label: 'SampleMatrix' },
  ] as const;
  type ColKey = typeof COC_COLS[number]['key'];
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({});
  const [openCol, setOpenCol] = useState<ColKey | null>(null);
  const [colMenuPos, setColMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [colMenuQ, setColMenuQ] = useState('');
  const cellVal = (r: CocPreLogRowDto, key: ColKey) => String((r as unknown as Record<string, unknown>)[key] ?? '');
  const distinctFor = (key: ColKey) =>
    Array.from(new Set(rows.map(r => cellVal(r, key)).filter(v => v !== '')))
      .sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    void api.get('/api/v1/sample-registration/coc-prelog', {
      params: { projectId: projectOid ?? undefined, clientId: clientOid ?? undefined },
      signal: ac.signal,
    })
      .then(res => setRows((res.data as ApiSuccess<CocPreLogRowDto[]>).data ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [projectOid, clientOid]);

  const shownRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter(r => {
      // Global search — any visible column.
      if (term && ![r.cocId, r.cocName, r.projectId, r.projectName, r.projectLocation, r.sampleMatrixNames]
        .some(v => String(v ?? '').toLowerCase().includes(term))) return false;
      // Per-column filters — each active column must include the row's value.
      for (const [key, vals] of Object.entries(colFilters)) {
        if (vals.length && !vals.includes(cellVal(r, key as ColKey))) return false;
      }
      return true;
    });
  }, [rows, q, colFilters]);

  const toggleColMenu = (key: ColKey, e: ReactMouse) => {
    e.stopPropagation();
    if (openCol === key) { setOpenCol(null); return; }
    const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const mw = 220;
    setColMenuPos({ top: b.bottom + 3, left: Math.max(8, Math.min(b.left, window.innerWidth - mw - 8)) });
    setColMenuQ('');
    setOpenCol(key);
  };
  const toggleColValue = (key: ColKey, v: string) => setColFilters(f => {
    const cur = f[key] ?? [];
    const next = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v];
    return { ...f, [key]: next };
  });

  const doImport = async () => {
    if (!selected) { onWarn('Select a Pre-Log record to import.'); return; }
    setImporting(true);
    try {
      // For a new/unsaved job, create it now applying the chosen pre-log's
      // matrix (legacy: the matrix comes from the pre-log, never a prompt).
      let oid = jobOid;
      if (!oid) {
        const chosen = rows.find(r => r.oid === selected);
        const matrixNames = (chosen?.sampleMatrixNames ?? '')
          .split(/[;,]/).map(s => s.trim()).filter(Boolean);
        oid = await ensureJobOid(matrixNames);
      }
      const res = await api.post(`/api/v1/sample-registration/${encodeURIComponent(oid)}/import-coc-prelog`,
        { cocSettingsOid: selected });
      const data = (res.data as ApiSuccess<{ samplesCount: number; testsCount: number; bottlesCount: number; cocId: string }>).data;
      onImported({
        cocId: data.cocId, samplesCount: data.samplesCount,
        testsCount: data.testsCount, bottlesCount: data.bottlesCount,
      });
    } catch (e) {
      onWarn(e instanceof Error ? e.message : 'Failed to import the Pre-Log record.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <AppDialog open width={860} icon="ti-download" title="Import Pre-Log" onClose={onClose}
      footer={<>
        <button className="ca-btn ca-btn-solid" disabled={!selected || importing} onClick={() => void doImport()}>
          {importing ? 'Importing…' : 'OK'}
        </button>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      {!projectOid || !clientOid ? (
        <div className="sr-hint-inline">Select a Client and Project on the Registration form first.</div>
      ) : (
        <>
          {/* "Text to search…" box — legacy Import Pre-Log popup parity. */}
          <div className="sr-pick-search">
            <input placeholder="Text to search…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
            <span className="sr-pick-search-i"><i className="ti ti-search" /></span>
          </div>
          <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
            <table className="sr-pick-table">
              {/* Column captions match the legacy Import Pre-Log grid verbatim
                  (BTLIMS.Web\Model.xafml, COCSettings_ListView_SampleRegistration_ImportCOC):
                  COC_ID · COCName · ProjectID · ProjectName · ProjectLocation ·
                  SampleMatrix, sorted descending by COC_ID (server-side). */}
              <thead>
                <tr>
                  <th></th>
                  {COC_COLS.map(c => (
                    <th key={c.key}>
                      <span className="sr-pick-th-lbl">{c.label}</span>
                      <button type="button"
                        className={`sr-pick-colf-arrow${(colFilters[c.key]?.length ?? 0) > 0 ? ' on' : ''}`}
                        title="Filter" onClick={e => toggleColMenu(c.key, e)}>
                        <i className="ti ti-chevron-down" />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7}>Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={7}>No Pre-Log records found for this Client/Project.</td></tr>}
                {!loading && rows.length > 0 && shownRows.length === 0 && <tr><td colSpan={7}>No matches for “{q}”.</td></tr>}
                {!loading && shownRows.map(r => (
                  <tr key={r.oid} onClick={() => setSelected(r.oid)}
                    className={selected === r.oid ? 'on' : undefined}>
                    <td><input type="radio" checked={selected === r.oid} onChange={() => setSelected(r.oid)} /></td>
                    <td>{r.cocId}</td>
                    <td>{r.cocName}</td>
                    <td>{r.projectId}</td>
                    <td>{r.projectName}</td>
                    <td>{r.projectLocation}</td>
                    <td>{r.sampleMatrixNames}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Per-column filter menu (Excel-style: search + (All)/(Clear) +
              distinct-value checklist). Fixed-positioned so it escapes the
              table's scroll clip. */}
          {openCol && colMenuPos && (() => {
            const key = openCol;
            const values = distinctFor(key).filter(v => v.toLowerCase().includes(colMenuQ.trim().toLowerCase()));
            const sel = colFilters[key] ?? [];
            return (
              <>
                <div className="sr-pick-colmenu-scrim" onClick={() => setOpenCol(null)} />
                <div className="sr-pick-colmenu" style={{ top: colMenuPos.top, left: colMenuPos.left }}>
                  <div className="sr-pick-colmenu-s">
                    <i className="ti ti-search" />
                    <input autoFocus placeholder="Search…" value={colMenuQ} onChange={e => setColMenuQ(e.target.value)} />
                  </div>
                  <div className="tr-combo-actions" style={{ display: 'flex', gap: 12, padding: '5px 11px' }}>
                    <a style={{ cursor: 'pointer', color: 'var(--ai-blue)', fontWeight: 700 }}
                      onClick={() => setColFilters(f => ({ ...f, [key]: values }))}>Select all</a>
                    <a style={{ cursor: 'pointer', color: 'var(--ai-blue)', fontWeight: 700 }}
                      onClick={() => setColFilters(f => ({ ...f, [key]: [] }))}>Clear</a>
                  </div>
                  <div className="sr-pick-colmenu-list">
                    {values.length === 0 && <div className="sr-pick-colmenu-item">No values</div>}
                    {values.map(v => (
                      <label key={v} className="sr-pick-colmenu-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={sel.includes(v)} onChange={() => toggleColValue(key, v)} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        </>
      )}
    </AppDialog>
  );
}
